
/* ================================================================
   STAGE3D: zelfstandige 3D renderer voor Versie B (Rip Studio)
   Eigendom van deze agent. Niet door de UI-agent aangepast.

   Twee opbouwmodi:
   - 'box'  : echte kubus-mesh uit rechtgetrokken foto-vlakken (front/side/top),
              via een zelf geschreven DLT-homografie (geen dubbel perspectief).
              Open booster box (faces.flap + pose3d.fa/fe/fc): dezelfde doos met de open
              bovenkant (de pakjes) als top, plus een staand deksel achteraan (buildLid).
   - 'pack' : kussen-mesh voor frontale boosterpacks (zelfde stijl als de
              eerdere Studio-renderer, maar scherper en met properder masker).

   Publieke API (window.Stage3D):
     init()                      -> bool, true als WebGL beschikbaar is
     show(product, container, targetRect, opts) -> Promise<bool>, true als 3D actief werd; opts.fade = ms invagen
     retarget(targetRect, container) her-meet de kadrering zonder de scene opnieuw op te bouwen
                                  (voor app.js, bv. na een echte .stage-vormwijziging)
     detach()
     dragStart() / drag(dx) / dragEnd(vx)
     pointer(x, y)                normaliseerde muispositie -1..1
     requestGyro()                -> Promise<bool>, vraagt iOS-toestemming
     pause(bool)
     pose(yawRadians)
     owns(container)              -> bool
     prefetch(product)            warm de volgende scene alvast op
     dispose(id)                  ruim een niet meer gebruikt product op
     state()                      debug: huidige status

   product-vorm die deze module verwacht:
     { h, f, mode: 'pack'|'box', faces?: { front, side, top? } }
   faces staat genormaliseerd (0..1) t.o.v. de eigen 2048px-aanvraag van de foto.
   ================================================================ */
(function (global) {
'use strict';

/* THREE en gsap kunnen async geladen zijn (volgorde t.o.v. dit script niet gegarandeerd),
   dus nooit op scriptlaadtijd vastgrijpen: init() leest window.THREE/window.gsap pas op het
   moment dat hij echt aangeroepen wordt. Zo faalt de module nooit stil leeg bij een rastiming. */
const RM = global.matchMedia ? matchMedia('(prefers-reduced-motion: reduce)').matches : false;
const CDN = 'https://cdn.shopify.com/s/files/1/0970/1369/4813/files/';
/* data-URI's van de pack-cutouts (enkel de mode:'pack'-producten, door build.py ingebed als
   <script type="application/json" id="pack-cutouts">): onder file:// tilt Chrome elke lokale
   afbeelding als cross-origin zodra WebGL er een texture van wil maken (texSubImage2D weigert),
   ook met een <img>-bron zonder crossOrigin. Een data:-URI heeft geen origin en wordt dus nooit
   geweigerd. De platte foto elders op de pagina (.scard-ph, geen WebGL) laadt gewoon het bestand
   op schijf. application/json (geen uitvoerbare JS) + pas parsen bij de eerste 3D-pack: zo wacht
   het eerste beeld niet op de ~3,6 MB aan ingebedde tekst.
   Sinds 5 okt staan de data-URI's niet meer in de pagina maar in assets/embed-file.js (window.CW_EMBED),
   dat enkel onder file:// geladen wordt. Over http is pack-cutouts leeg en valt makePack terug op het bestand. */
let _packCutData = null;
/* HD-uitsneden van zij- en bovenvlakken (faces-hd, door build.py ingebed; ../upscale/faces_hd.py met
   SwinIR): pas bij de eerste doos geparset, net als packCutData. Over http paden naar https://cardixai-production.up.railway.app/cw-rs/cw-rs-test-f-. */
let FHD = null;
function facesHD() {
  if (!FHD && global.CW_EMBED && global.CW_EMBED.faces) FHD = global.CW_EMBED.faces;
  if (!FHD) { try { const el = document.getElementById('faces-hd'); FHD = el ? JSON.parse(el.textContent) : {}; } catch (e) { FHD = {}; } }
  /* automatische 3D (app.js LIVE3D): de live HD-vlakken gaan voor; een live product zonder HD-vlakken gebruikt ook geen oude */
  if (!FHD._live && global.CW_RS_3D_LIVE) {
    FHD._live = true;
    Object.keys(global.CW_RS_3D_LIVE).forEach(h => {
      const L = global.CW_RS_3D_LIVE[h]; if (!L || !L.data) return;
      const hd = L.data.hd, fu = L.urls && L.urls.f;
      if (!hd || !fu || L.data.nakijken) { delete FHD[h]; return; }
      const o = {}; Object.keys(hd).forEach(k => { if (fu[k] && hd[k] && hd[k].crop) o[k] = { crop:hd[k].crop, uri:fu[k] }; });
      FHD[h] = o;
    });
  }
  return FHD;
}
function packCutData() {
  if (_packCutData) return _packCutData;
  if (global.CW_EMBED && global.CW_EMBED.pack) return (_packCutData = global.CW_EMBED.pack);
  try { const el = global.document && document.getElementById('pack-cutouts'); _packCutData = el ? JSON.parse(el.textContent) : {}; }
  catch (e) { _packCutData = {}; }
  return _packCutData;
}
/* auto3d (6 okt): per product een eigen embed-script assets/embed-cut/<handle>.js (build.py) dat
   window.CW_EMBED_CUT[handle] = 'data:image/webp;base64,...' zet. Enkel onder file:// nodig en pas geladen
   als dat product echt in 3D getoond wordt, zodat de pagina zelf niet tientallen MB groter wordt. */
const LAZY_CUT = new Map();
function lazyCutData(handle) {
  const box = global.CW_EMBED_CUT || (global.CW_EMBED_CUT = {});
  if (box[handle]) return Promise.resolve(box[handle]);
  if (LAZY_CUT.has(handle)) return LAZY_CUT.get(handle);
  const pr = new Promise(res => {
    try {
      const s = document.createElement('script'); s.async = true; s.src = 'assets/embed-cut/' + handle + '.js';
      s.onload = () => res(box[handle] || null); s.onerror = () => res(null);
      document.head.appendChild(s);
    } catch (e) { res(null); }
  });
  LAZY_CUT.set(handle, pr); return pr;
}
const img = (f, w) => CDN + f + (f.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
const clamp =(v, a, b) => v < a ? a : v > b ? b : v;

/* ================= DLT homografie (4-punts correspondentie, eigen implementatie) =================
   Lost H (3x3, h8=1) op zodat H * [x,y,1] ~ [u,v,1] voor elk van de 4 puntparen.
   8 onbekenden uit 8 vergelijkingen, via Gauss-eliminatie met partiele pivotering. */
function solve8(A, b) {
  const n = 8; const M = A.map((row, i) => row.concat([b[i]]));
  for (let col = 0; col < n; col++) {
    let piv = col;
    for (let r = col + 1; r < n; r++) if (Math.abs(M[r][col]) > Math.abs(M[piv][col])) piv = r;
    const tmp = M[col]; M[col] = M[piv]; M[piv] = tmp;
    const pv = M[col][col] || 1e-9;
    for (let c = col; c <= n; c++) M[col][c] /= pv;
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = M[r][col]; if (!f) continue;
      for (let c = col; c <= n; c++) M[r][c] -= f * M[col][c];
    }
  }
  return M.map(row => row[n]);
}
function dltHomography(src4, dst4) {
  const A = [], b = [];
  for (let i = 0; i < 4; i++) {
    const [x, y] = src4[i], [u, v] = dst4[i];
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]); b.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]); b.push(v);
  }
  const h = solve8(A, b);
  return [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1];
}
function applyH(H, x, y) {
  const w = H[6] * x + H[7] * y + H[8];
  return [(H[0] * x + H[1] * y + H[2]) / w, (H[3] * x + H[4] * y + H[5]) / w];
}
const dist = (a, b) => Math.hypot(b[0] - a[0], b[1] - a[1]);

/* ================= rechttrekken: per-pixel inverse-mapping met bilineaire sampling =================
   quad = [BL, BR, TR, TL] in bronpixels. Canvas-ruimte (0,0) is linksboven, dus BL -> (0,th),
   BR -> (tw,th), TR -> (tw,0), TL -> (0,0): bovenrand van de uitvoer = bovenrand van het vlak.
   forcedAspect (tw/th), indien gegeven: de ECHTE (opgeloste) verhouding van het vlak, niet de
   verhouding van de gefotografeerde (verkorte) pixelranden. Op een 3/4-foto is het front
   vooraanzicht-breedte altijd verkort t.o.v. de werkelijke breedte; zonder forcedAspect levert dat
   een te smal/uitgerekt front op (precies de klacht "front te smal"). Met forcedAspect blijft het
   RECHTHOEK-doel (tw,th) van de homografie op de juiste verhouding staan, en trekt de DLT-homografie
   het perspectief correct recht i.p.v. de verkorting gewoon te kopieren. */
function rectifyQuad(srcData, srcW, srcH, quad, longEdge, forcedAspect) {
  const [BL, BR, TR, TL] = quad;
  let tw, th;
  if (forcedAspect) {
    if (forcedAspect >= 1) { tw = Math.round(clamp(longEdge, 480, 2048)); th = Math.max(8, Math.round(tw / forcedAspect)); }
    else { th = Math.round(clamp(longEdge, 480, 2048)); tw = Math.max(8, Math.round(th * forcedAspect)); }
  } else {
    const w = (dist(BL, BR) + dist(TL, TR)) / 2;
    const h = (dist(BL, TL) + dist(BR, TR)) / 2;
    if (w >= h) { tw = Math.round(clamp(longEdge, 480, 2048)); th = Math.max(8, Math.round(tw * h / w)); }
    else { th = Math.round(clamp(longEdge, 480, 2048)); tw = Math.max(8, Math.round(th * w / h)); }
  }
  const rect4 = [[0, th], [tw, th], [tw, 0], [0, 0]];
  const H = dltHomography(rect4, quad);
  const out = new Uint8ClampedArray(tw * th * 4);
  const gx = (xx, yy, k) => {
    xx = xx < 0 ? 0 : xx >= srcW ? srcW - 1 : xx; yy = yy < 0 ? 0 : yy >= srcH ? srcH - 1 : yy;
    return srcData[(yy * srcW + xx) * 4 + k];
  };
  for (let y = 0; y < th; y++) {
    for (let x = 0; x < tw; x++) {
      const [sx, sy] = applyH(H, x + .5, y + .5);
      const x0 = Math.floor(sx - .5), y0 = Math.floor(sy - .5), fx = sx - .5 - x0, fy = sy - .5 - y0;
      const j = (y * tw + x) * 4;
      for (let k = 0; k < 4; k++) {
        const v = (gx(x0, y0, k) * (1 - fx) + gx(x0 + 1, y0, k) * fx) * (1 - fy) +
          (gx(x0, y0 + 1, k) * (1 - fx) + gx(x0 + 1, y0 + 1, k) * fx) * fy;
        out[j + k] = v;
      }
    }
  }
  return { data: out, w: tw, h: th };
}

/* ================= boostpack-masker: rechthoekige krimptand-vorm, zacht uitgeveerd =================
   Geen witte halo: de rand wordt via een sub-pixel gradient-drempel verzacht (3 px), niet hard geknipt. */
function packMask(a, W, H) {
  let br = 0, bg = 0, bb = 0, n = 0;
  for (let y = 0; y < 6; y++) for (let x = 0; x < 6; x++) { const j = (y * W + x) * 4; br += a[j]; bg += a[j + 1]; bb += a[j + 2]; n++; }
  br /= n; bg /= n; bb /= n;
  const diff = new Float32Array(W * H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) { const j = (y * W + x) * 4; diff[y * W + x] = Math.max(Math.abs(a[j] - br), Math.abs(a[j + 1] - bg), Math.abs(a[j + 2] - bb)); }
  const col = new Float32Array(W), row = new Float32Array(H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) { if (diff[y * W + x] > 10) { col[x]++; row[y]++; } }
  let x0 = 0, x1 = W - 1, y0 = 0, y1 = H - 1;
  while (x0 < W - 1 && col[x0] < H * .02) x0++; while (x1 > x0 && col[x1] < H * .02) x1--;
  while (y0 < H - 1 && row[y0] < W * .02) y0++; while (y1 > y0 && row[y1] < W * .02) y1--;
  const FE = 3;
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const j = (y * W + x) * 4;
      const dxL = x - x0, dxR = x1 - x, dyT = y - y0, dyB = y1 - y;
      const edge = Math.min(dxL, dxR, dyT, dyB);
      const al = edge < 0 ? 0 : edge >= FE ? 1 : edge / FE;
      a[j + 3] = Math.round(al * 255);
    }
  }
  return { minx: Math.max(0, x0 - 1), miny: Math.max(0, y0 - 1), maxx: Math.min(W - 1, x1 + 1), maxy: Math.min(H - 1, y1 + 1) };
}

/* ================= verwerk-worker: vrijstaand maken (pack) of vlakken rechttrekken (box) =================
   Draait los van het hoofdscherm zodat 60fps niet in het gedrang komt bij het wisselen van scene. */
/* elke helper die rectifyQuad/packMask gebruiken MOET hier mee: ontbrak clamp, dan faalde elke
   worker-job stil ('clamp is not defined') en rekende de hoofdthread 300 ms per doos (haperen) */
const WORKER_SRC = 'const clamp = ' + clamp.toString() + ';\n' + 'const dist = ' + dist.toString() + ';\n' +
  'const solve8 = ' + solve8.toString() + ';\n' +
  'const dltHomography = ' + dltHomography.toString() + ';\n' +
  'const applyH = ' + applyH.toString() + ';\n' +
  'const rectifyQuad = ' + rectifyQuad.toString() + ';\n' +
  'const packMask = ' + packMask.toString() + ';\n' +
  'self.onmessage = async (e) => {' +
  '  const { id, kind, url, faces, aspects } = e.data;' +
  '  try {' +
  '    const r = await fetch(url, { mode:"cors" }); if (!r.ok) throw new Error("http " + r.status);' +
  '    const bmp = await createImageBitmap(await r.blob(), { imageOrientation:"from-image" }); const W = bmp.width, H = bmp.height;' +
  '    const c = new OffscreenCanvas(W, H); const ctx = c.getContext("2d", { willReadFrequently:true }); ctx.drawImage(bmp, 0, 0);' +
  '    const data = ctx.getImageData(0, 0, W, H).data;' +
  '    if (kind === "pack") {' +
  '      const k = packMask(data, W, H); ctx.putImageData(new ImageData(data, W, H), 0, 0);' +
  '      const cw = k.maxx - k.minx + 1, ch = k.maxy - k.miny + 1;' +
  '      const o = new OffscreenCanvas(cw, ch); o.getContext("2d").drawImage(c, k.minx, k.miny, cw, ch, 0, 0, cw, ch);' +
  '      const bm = o.transferToImageBitmap(); self.postMessage({ id, ok:true, kind, img:bm, w:cw, h:ch }, [bm]); return;' +
  '    }' +
  '    const out = {}; const transfer = []; const longEdge = e.data.longEdge || { front:1600, side:1280, top:1280 };' +
  '    for (const key of Object.keys(faces)) {' +
  '      const quad = faces[key].map(p => [p[0] * W, p[1] * H]);' +
  '      const rec = rectifyQuad(data, W, H, quad, longEdge[key] || 1280, aspects && aspects[key]);' +
  '      const oc = new OffscreenCanvas(rec.w, rec.h); oc.getContext("2d").putImageData(new ImageData(rec.data, rec.w, rec.h), 0, 0);' +
  '      const bm = oc.transferToImageBitmap();' +
  '      let r2 = 0, g2 = 0, b2 = 0, cnt = 0;' +
  '      for (let i = 0; i < rec.data.length; i += 97 * 4) { r2 += rec.data[i]; g2 += rec.data[i + 1]; b2 += rec.data[i + 2]; cnt++; }' +
  '      out[key] = { img:bm, w:rec.w, h:rec.h, avg:cnt ? [r2 / cnt | 0, g2 / cnt | 0, b2 / cnt | 0] : [90, 70, 120] };' +
  '      transfer.push(bm);' +
  '    }' +
  '    self.postMessage({ id, ok:true, kind, faces:out }, transfer);' +
  '  } catch (err) { self.postMessage({ id, ok:false, err:String(err && err.message || err) }); }' +
  '};';

let WK = null; const WJOBS = new Map(); let WID = 0;
function initWorker() {
  try {
    if (global.Worker && global.OffscreenCanvas && global.createImageBitmap) {
      WK = new Worker(URL.createObjectURL(new Blob([WORKER_SRC], { type: 'text/javascript' })));
      WK.onmessage = e => { const j = WJOBS.get(e.data.id); if (j) { WJOBS.delete(e.data.id); j(e.data); } };
      WK.onerror = () => { WK = null; WJOBS.forEach(j => j({ ok: false })); WJOBS.clear(); };
    }
  } catch (e) { WK = null; }
}
/* hoofdthread-terugvalpad (geen Worker/OffscreenCanvas): zelfde algoritmes, gewoon synchroon */
async function mainThreadJob(payload) {
  try {
    const { kind, url, faces, aspects } = payload;
    const im = new Image(); im.crossOrigin = 'anonymous'; im.decoding = 'async'; im.src = url; await im.decode();
    const W = im.naturalWidth, H = im.naturalHeight;
    const c = document.createElement('canvas'); c.width = W; c.height = H;
    const ctx = c.getContext('2d', { willReadFrequently: true }); ctx.drawImage(im, 0, 0);
    const data = ctx.getImageData(0, 0, W, H).data;
    if (kind === 'pack') {
      const k = packMask(data, W, H); ctx.putImageData(new ImageData(data, W, H), 0, 0);
      const cw = k.maxx - k.minx + 1, ch = k.maxy - k.miny + 1;
      const o = document.createElement('canvas'); o.width = cw; o.height = ch;
      o.getContext('2d').drawImage(c, k.minx, k.miny, cw, ch, 0, 0, cw, ch);
      return { ok: true, kind, img: o, w: cw, h: ch };
    }
    const out = {}; const longEdge = payload.longEdge || { front: 1600, side: 1280, top: 1280 };
    for (const key of Object.keys(faces)) {
      const quad = faces[key].map(p => [p[0] * W, p[1] * H]);
      const rec = rectifyQuad(data, W, H, quad, longEdge[key] || 1280, aspects && aspects[key]);
      const oc = document.createElement('canvas'); oc.width = rec.w; oc.height = rec.h;
      oc.getContext('2d').putImageData(new ImageData(rec.data, rec.w, rec.h), 0, 0);
      let r2 = 0, g2 = 0, b2 = 0, cnt = 0;
      for (let i = 0; i < rec.data.length; i += 97 * 4) { r2 += rec.data[i]; g2 += rec.data[i + 1]; b2 += rec.data[i + 2]; cnt++; }
      out[key] = { img: oc, w: rec.w, h: rec.h, avg: cnt ? [r2 / cnt | 0, g2 / cnt | 0, b2 / cnt | 0] : [90, 70, 120] };
    }
    return { ok: true, kind, faces: out };
  } catch (e) { return { ok: false, err: String(e && e.message || e) }; }
}
async function runJob(payload, timeoutMs) {
  if (WK) {
    const r = await new Promise(res => {
      const id = ++WID; WJOBS.set(id, res); WK.postMessage(Object.assign({ id }, payload));
      setTimeout(() => { if (WJOBS.has(id)) { WJOBS.delete(id); res({ ok: false, err: 'timeout' }); } }, timeoutMs || 12000);
    });
    if (r && r.ok) return r;
    if (global.console) console.warn('[Stage3D] worker-job mislukt, terugval op hoofdthread:', r && r.err);
  }
  return mainThreadJob(payload);
}

/* ================= kleine meetkunde-helpers voor vlak-afmetingen ================= */
function quadW(q) { return (dist(q[0], q[1]) + dist(q[3], q[2])) / 2; }
function quadH(q) { return (dist(q[0], q[3]) + dist(q[1], q[2])) / 2; }
function detectHand(front, side) {
  const dL = dist(front[0], side[1]) + dist(front[3], side[2]);
  const dR = dist(front[1], side[0]) + dist(front[2], side[3]);
  return dL <= dR ? 'L' : 'R';
}
function mixColor(avg, k) { return [avg[0] * k, avg[1] * k, avg[2] * k]; }
function avgColor(a, b) { return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2]; }
function rgbHex(c) {
  const r = clamp(Math.round(c[0]), 0, 255), g = clamp(Math.round(c[1]), 0, 255), b = clamp(Math.round(c[2]), 0, 255);
  return (r << 16) | (g << 8) | b;
}

/* ================= kwaliteitsniveau: oudere laptops en zwakke gsm's =================
   Eenmalig bepaald uit de GPU-naam (WEBGL_debug_renderer_info), het aantal kernen, het geheugen en
   Save-Data. Wat elk niveau doet (Q hieronder):
   - high: pixelratio tot 2, anti-aliasing, volle glans (clearcoat), glanslijn en stofjes, 2 3D-vensters
   - mid : pixelratio tot 1.5, eenvoudiger materiaal (geen clearcoat), kleinere texturen
   - low : pixelratio 1, geen anti-aliasing, geen glanslijn of stofjes, max 30 fps, 1 3D-venster
   Bovenop het niveau verlaagt de 3D zelf de pixelratio als hij in de praktijk onder ~25 fps zakt
   (zie monitor in tick). Testen kan met ?q3d=low|mid|high in de URL. */
const Q = {
  high: { dpr:2,   aa:true,  phys:true,  fx:true,  aniso:8, tex:1,   fps:60, spare:true  },
  mid:  { dpr:1.5, aa:true,  phys:false, fx:true,  aniso:4, tex:.8,  fps:60, spare:true  },
  low:  { dpr:1,   aa:false, phys:false, fx:false, aniso:1, tex:.64, fps:30, spare:false }
};
let TIER = null;
function detectTier() {
  if (TIER) return TIER;
  let gpu = '';
  try {
    const c = document.createElement('canvas'); const gl = c.getContext('webgl');
    if (gl) {
      const ext = gl.getExtension('WEBGL_debug_renderer_info');
      gpu = String((ext && gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)) || gl.getParameter(gl.RENDERER) || '');
      const lose = gl.getExtension('WEBGL_lose_context'); if (lose) lose.loseContext();
    }
  } catch (e) {}
  const g = gpu.toLowerCase(), nav = global.navigator || {};
  const cores = nav.hardwareConcurrency || 4, mem = nav.deviceMemory || 8, save = !!(nav.connection && nav.connection.saveData);
  /* oude/zwakke GPU's: software-rendering, Intel HD (niet UHD/Iris Xe), oudere Mali/Adreno/PowerVR */
  const slow = /swiftshader|llvmpipe|softpipe|software|basic render|(^|[^u])hd graphics|gma |mali-(4|t)|mali-g(31|51|52|57|68)|adreno[^0-9]*[2-5]\d\d|powervr|sgx/.test(g);
  const fast = /apple m\d|apple gpu|rtx|gtx 1[0-9]|radeon rx|radeon pro|adreno[^0-9]*[67]\d\d|iris\(r\) xe|iris xe|arc\(tm\)|arc a/.test(g);
  let tier = 'mid';
  if (save || slow || cores <= 2 || mem <= 2) tier = 'low';
  else if (fast && cores >= 6 && mem >= 4) tier = 'high';
  else if (cores <= 4 && mem <= 4) tier = 'low';
  try { const o = new URLSearchParams(global.location.search).get('q3d'); if (o && Q[o]) tier = o; } catch (e) {}
  TIER = { tier, gpu, cores, mem };
  return TIER;
}

global.Stage3D = buildStage3D();
/* tweede, onafhankelijke instantie (eigen WebGL-canvas): app.js zet daarmee het volgende product al
   klaar terwijl je nog naar het huidige kijkt, zodat de 3D er bij aankomst meteen staat */
global.buildStage3D = buildStage3D;
function buildStage3D() {
  /* T2 en G worden pas bij init() ingevuld (zie onder): THREE/gsap kunnen na dit script laden. */
  let T2, G;
  let renderer, scene, camera, root, holder, dust, env, maxAniso = 4;
  let host = null, cur = null, raf = 0, last = 0, token = 0, ro = null, calRT = null;
  let QL = Q.mid; /* kwaliteit van deze instantie, gezet in init() */
  const MON = { n: 0, buf: [], lastRender: 0, acc: 0 };
  const cache = new Map();
  /* px/py: genormaliseerde pointerpositie TEN OPZICHTE VAN DE STAGE (-.5..5, 0 = gecentreerd/weg),
     gezet door app.js. hoverYaw/hoverPitch zijn de gladgestreken, begrensde offset die daaruit volgt
     (zie tick()): een kleine optelling bovenop de heldpose, nooit een vervanging ervan. */
  const S = { t: 0, drag: 0, enter: 0, paused: false, px: 0, py: 0, hoverYaw: 0, hoverPitch: 0, gBeta: 0, gGamma: 0, gOn: false, baseX: 0, baseY: .06, follow: null, vh: 0, still: false, stillFrames: 0, prScale: 1 };
  /* begrenzing pointer/hover-tilt (en gyro, zie tick()): max ~10 graden yaw, ~6 graden pitch rond
     de heldpose. Math.PI-breuken i.p.v. T2.MathUtils: T2 is pas na init() gevuld. */
  const YAW_MAX = Math.PI / 18, PITCH_MAX = Math.PI / 30;
  /* max uitslag van een actieve sleep-draai (rad) rond de heldpose: ver genoeg om de achterkant te
     zien, niet oneindig. dragEnd() rondt af naar het dichtstbijzijnde veelvoud van 2*PI, dus binnen
     dit bereik veert elke sleep altijd terug naar S.drag = 0, oftewel de heldpose. */
  const DRAG_MAX = 2.3;
  const VS = 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }';

  const radialTex = (inner, outer) => {
    const c = document.createElement('canvas'); c.width = c.height = 128; const x = c.getContext('2d');
    const g = x.createRadialGradient(64, 64, 0, 64, 64, 64); g.addColorStop(0, inner); g.addColorStop(1, outer);
    x.fillStyle = g; x.fillRect(0, 0, 128, 128); return new T2.CanvasTexture(c);
  };
  function envScene() {
    const s = new T2.Scene();
    s.add(new T2.Mesh(new T2.SphereGeometry(10, 32, 16), new T2.MeshBasicMaterial({ color: 0x241a3d, side: T2.BackSide })));
    const panel = (w, h, x, y, z, c, k) => {
      const m = new T2.Mesh(new T2.PlaneGeometry(w, h), new T2.MeshBasicMaterial({ color: new T2.Color(c).multiplyScalar(k), side: T2.DoubleSide }));
      m.position.set(x, y, z); m.lookAt(0, 0, 0); s.add(m);
    };
    panel(1.8, 8, -4.4, .6, 2.2, 0x7B5EA7, 2.6);
    panel(1.4, 7.4, 4.6, .3, 1.6, 0xE8C840, 2.1);
    panel(8, 1.4, 0, 5.2, 1.2, 0xffffff, 1.4);
    panel(3.2, 2.2, 1.6, 1.1, 6.4, 0xffffff, .85);
    return s;
  }
  function makeTex(src, flip) {
    const tex = new T2.Texture(src);
    tex.encoding = T2.sRGBEncoding; tex.anisotropy = Math.min(maxAniso, QL.aniso);
    tex.minFilter = T2.LinearMipmapLinearFilter; tex.magFilter = T2.LinearFilter;
    tex.generateMipmaps = true;
    /* bekende eigenaardigheid: ImageBitmap-bronnen op een platte PlaneGeometry (de pack-pillow)
       negeren texture.flipY in deze renderer, dus de rij-volgorde wordt via de UV-matrix
       gecompenseerd. Ook de boxvlakken uit de worker zijn ImageBitmaps en krijgen dezelfde compensatie
       (zie buildBoxProduct); enkel canvas-bronnen (hoofdthread-terugval) volgen flipY wel. */
    if (flip) { tex.repeat.set(1, -1); tex.offset.set(0, 1); }
    tex.needsUpdate = true;
    return tex;
  }
  function physMat(map, color) {
    /* de foto-textuur bevat zijn eigen belichting al: omgevingsreflectie en clearcoat leggen daar een
       grijze waas over (gemeten: donkere dozen tot 36% lichter dan de foto). Laag gehouden zodat de 3D
       dezelfde kleur heeft als de foto, met nog een subtiele glans bij het kantelen. */
    const o = { map: map || null, color: map ? 0xffffff : (color != null ? color : 0x6a5a86), roughness: .42, metalness: .05, envMap: env, envMapIntensity: .16 };
    /* clearcoat = een tweede lichtlaag per pixel: mooi, maar zwaar op oudere GPU's -> enkel op 'high' */
    return QL.phys ? new T2.MeshPhysicalMaterial(Object.assign(o, { clearcoat: .18, clearcoatRoughness: .3 })) : new T2.MeshStandardMaterial(Object.assign(o, { roughness: .4 }));
  }
  /* ================= boxbouwer: 6 materialen in de standaard BoxGeometry-groepsvolgorde
     [+x, -x, +y, -y, +z, -z]. front -> +z, back (ongefotografeerd) -> -z effen paneel,
     side -> de kant waar de foto hem toont, mirror -> gespiegelde kopie aan de overkant. ================= */
  async function buildBoxProduct(product) {
    /* zonder zijvlak (open display recht van voren en van boven, auto3d2): zijkanten effen in de kleur van de voorkant */
    const faces = product.faces; const hand = faces.side ? detectHand(faces.front, faces.side) : 'R';
    const url = img(product.f, 2048);
    /* pose3d: per product offline opgelost (solve_pose.py, context van deze fix: "front te smal /
       verkeerde zijkant") via reprojectie-least-squares tegen de geannoteerde hoekpunten -- een
       echt camera/doosmodel, geen pixelschatting. Geeft de WERKELIJKE w:h:d-verhouding (H=1) en de
       heldpose (yaw/pitch) die de foto exact reproduceert. Zonder pose3d (nieuw product, nog niet
       opgelost) valt dit terug op de oude vuistregel, met het tekenprobleem gecorrigeerd. */
    const pose = product.pose3d;
    /* open booster box (faces.flap + pose3d.fe/fc, zie buildLid): het lichaam is dezelfde doos als
       hieronder, de bovenkant toont de open inhoud (de pakjes), en achteraan staat het deksel als eigen
       paneel. Het deksel-vlak gaat gewoon mee in dezelfde worker-job als front/side/top. */
    const open = !!(faces.flap && pose && pose.fe > 0 && pose.fc && pose.fc.length > 2);
    /* pose3d.sr (toonbankdisplay, auto3d2): de zijwanden zijn 1 + sr keer zo hoog als de voorrand; faces.side is dan
       het volle zijwandvlak (zie de vleugels hieronder) */
    const sr = pose && pose.sr > 0 ? pose.sr : 0;
    const aspects = pose ? { front: pose.w / pose.h, side: pose.d / (pose.h * (1 + sr)), top: pose.w / pose.d } : null;
    const le = { front: Math.round(1600 * QL.tex), side: Math.round(1280 * QL.tex), top: Math.round(1280 * QL.tex) };
    if (open) { aspects.flap = pose.w / pose.fe; le.flap = Math.round(1280 * QL.tex); }
    const r = await runJob({ kind: 'box', url, faces, aspects, longEdge: le }, 16000);
    if (!r.ok) return null;
    const F = r.faces;
    /* zij- en bovenvlak scherp: op de 1200 px-productfoto staan die schuin en smal (100-200 px breed),
       rechtgetrokken werden ze 3-5x opgerekt en wazig. Zelfde vlak, zelfde worker-code, maar uit een
       vooraf 4x opgeschaalde uitsnede (SwinIR). Valt het vlak buiten de uitsnede of mislukt de job, dan
       blijft gewoon het vlak uit de productfoto staan. */
    const hd = facesHD()[product.h];
    if (hd) {
      /* rest12 (7 okt): ook een HD-voorvlak als faces-hd er een heeft (wazige bronfoto's, bv. Astral Radiance en
         Darkness Ablaze ETB: het front verkleind en met SwinIR opnieuw opgeschaald, scherper dan de 2048 px-foto) */
      await Promise.all(['front', 'side', 'top'].map(k => {
        const e = hd[k], q = faces[k]; if (!e || !q || !F[k]) return null;
        const [cx0, cy0, cx1, cy1] = e.crop, cw = cx1 - cx0, ch = cy1 - cy0;
        const mq = q.map(p => [(p[0] - cx0) / cw, (p[1] - cy0) / ch]);
        if (!mq.every(p => p[0] > -.002 && p[0] < 1.002 && p[1] > -.002 && p[1] < 1.002)) return null;
        /* absoluut maken: de worker draait op een blob:-URL, daar lost een relatief pad (https://cardixai-production.up.railway.app/cw-rs/cw-rs-test-f-...) niet op */
        return runJob({ kind: 'box', url: new URL(e.uri, global.location.href).href, faces: { [k]: mq }, aspects, longEdge: le }, 16000)
          .then(r2 => { if (r2 && r2.ok && r2.faces && r2.faces[k]) { const old = F[k].img; F[k] = r2.faces[k]; if (old && old.close) old.close(); } }).catch(() => {});
      }));
    }
    let w, h, d;
    if (pose) {
      w = pose.w; h = pose.h; d = pose.d;
    } else {
      const wN = [quadW(faces.front)].concat(faces.top ? [quadW(faces.top)] : []);
      const hN = [quadH(faces.front), quadH(faces.side)];
      const dN = [quadW(faces.side)].concat(faces.top ? [quadH(faces.top)] : []);
      const avg = arr => arr.reduce((a, b) => a + b, 0) / arr.length;
      w = avg(wN); h = avg(hN); d = avg(dN);
    }
    const s = 1.9 / h; w *= s; d *= s; h = 1.9;
    const geo = new T2.BoxGeometry(w, h, d);
    /* de worker levert ImageBitmaps; daarbij negeert WebGL texture.flipY, dus de vlakken stonden
       ondersteboven zodra de worker echt werkte (eerder viel hij stil terug op canvassen, die flipY
       wel volgen). Zelfde UV-compensatie als bij de packs (makeTex flip); de spiegelzijde hieronder
       erft die van sideTex via clone(). */
    const bm = typeof ImageBitmap !== 'undefined' && F.front.img instanceof ImageBitmap;
    const frontTex = makeTex(F.front.img, bm), sideTex = F.side ? makeTex(F.side.img, bm) : null;
    const topTex = F.top ? makeTex(F.top.img, bm) : null;
    const bottomColor = rgbHex(mixColor(F.front.avg, .38));
    const topColor = rgbHex(mixColor(F.side ? avgColor(F.front.avg, F.side.avg) : F.front.avg, .72));
    const mats = new Array(6);
    const sideIdx = hand === 'L' ? 1 : 0, mirrorIdx = hand === 'L' ? 0 : 1;
    /* beide zijkanten met de gewone zijafbeelding: BoxGeometry zet die op elk zijvlak rechtop en
       leesbaar. Vroeger kreeg de verborgen zijkant een spiegelbeeld (tekst achterstevoren bij draaien). */
    const sideM = () => sideTex ? physMat(sideTex) : physMat(null, rgbHex(mixColor(F.front.avg, .8)));
    mats[sideIdx] = sideM(); mats[mirrorIdx] = sideM();
    mats[2] = topTex ? physMat(topTex) : physMat(null, topColor);
    mats[3] = physMat(null, bottomColor);
    mats[4] = physMat(frontTex);
    /* achterkant: dezelfde afbeelding als de voorkant, zodat de doos bij het draaien nooit een leeg
       vlak toont. BoxGeometry legt de uv's per vlak zo dat het beeld van buitenaf rechtop en leesbaar
       staat, dus van achteren gewoon juist (niet gespiegeld). */
    mats[5] = physMat(frontTex);
    if (sr && sideTex) {
      /* zijwand: onderste deel (lichaamshoogte) op de doos zelf, bovenste deel als eigen wandpaneel erboven */
      const f = 1 / (1 + sr), part = (lo, hi) => { const t = sideTex.clone(); t.needsUpdate = true;
        if (bm) { t.repeat.set(1, -(hi - lo)); t.offset.set(0, 1 - lo); } else { t.repeat.set(1, hi - lo); t.offset.set(0, lo); } return t; };
      const lower = physMat(part(0, f)); mats[sideIdx] = lower; mats[mirrorIdx] = lower;
      sideTex.userData.wing = part(f, 1);
    }
    const mesh = new T2.Mesh(geo, mats);
    const group = new T2.Group(); group.add(mesh);
    if (sr && sideTex) {
      const wm = physMat(sideTex.userData.wing); wm.side = T2.DoubleSide;
      for (const sgn of [-1, 1]) {
        /* schuine bovenrand: een driehoek van de hoge achterkant naar de voorrand (zoals de echte toonbankdisplay) */
        const bx = sgn > 0 ? d / 2 : -d / 2, fx = -bx, hy = h * sr / 2;
        const P3 = [fx, -hy, 0, bx, -hy, 0, bx, hy, 0], gW = new T2.BufferGeometry();
        gW.setAttribute('position', new T2.Float32BufferAttribute(P3, 3));
        gW.setAttribute('uv', new T2.Float32BufferAttribute([fx / d + .5, 0, bx / d + .5, 0, bx / d + .5, 1], 2));
        gW.setIndex(sgn > 0 ? [0, 1, 2] : [0, 2, 1]); gW.computeVertexNormals();
        const wing = new T2.Mesh(gW, wm);
        wing.rotation.y = sgn * Math.PI / 2; wing.position.set(sgn * w / 2, h / 2 + h * sr / 2, 0); group.add(wing);
      }
    }
    if (open && F.flap) { const lid = buildLid(F.flap, pose, w, h, d, bm); if (lid) group.add(lid); }
    const sweepGeo = new T2.BoxGeometry(w * 1.008, h * 1.008, d * 1.008);
    const sweepMat2 = new T2.ShaderMaterial({
      uniforms: { prog: { value: -3 }, dimW: { value: w }, dimH: { value: h }, dimD: { value: d } },
      vertexShader: 'varying vec3 vP; void main(){ vP = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: 'uniform float prog; uniform float dimW, dimH, dimD; varying vec3 vP; void main(){ float t = vP.x / dimW + vP.y / dimH * 0.6 + vP.z / dimD * 0.3; float d = t - prog; float b = exp(-d*d*22.0) * 0.22 + exp(-d*d*260.0) * 0.26; gl_FragColor = vec4(vec3(1.0, 0.97, 0.9), b); }',
      transparent: true, depthWrite: false, blending: T2.AdditiveBlending
    });
    const sweepMesh = new T2.Mesh(sweepGeo, sweepMat2); sweepMesh.renderOrder = 3; sweepMesh.userData.isEffect = true; if (QL.fx) group.add(sweepMesh);
    /* heldpose: uit pose3d (exact dezelfde kant en hoek als de foto, zie hierboven) als die er is.
       Zonder pose3d: de oude vuistregel (~27.5 graden), nu met het juiste teken -- positieve yaw
       toont in deze BoxGeometry-groepsvolgorde de -X (linker) zijkant, negatieve yaw de +X
       (rechter), dus 'L' (zijkant links op de foto, zie detectHand) hoort bij POSITIEVE yaw. De
       oude code had dit net omgekeerd, waardoor de verkeerde kant (de gespiegelde/verzonnen zijde)
       in beeld kwam. */
    const heroYaw = pose ? pose.yaw : (hand === 'L' ? 1 : -1) * .48;
    const heroPitch = pose ? pose.pitch : 0;
    const obj = { type: 'box', group, w, h, d, heroYaw, heroPitch, sweep: QL.fx ? { material: sweepMat2 } : null, sweepT: -.6 };
    if (open) obj.rotXY = true; /* zie realProjectedBox */
    return obj;
  }

  /* ================= deksel van een open booster box =================
     pose3d.fc is de omtrek van het deksel in zijn eigen vlak (u 0..1 over de doosbreedte, v 0..1 van het
     scharnier tot het hoogste punt, offline uit het productmasker gesneden, zie boxes3d/disp/build_open.py),
     pose3d.fe de hoogte van dat hoogste punt in doos-hoogtes en pose3d.fa de kanteling naar achter (rad).
     Echte geometrie i.p.v. een alfa-uitsnede: voorvlak + achtervlak + rand (dikte t), zodat het deksel bij
     het draaien nooit papierdun of leeg is. De textuur (faces.flap) is de rechtgetrokken rechthoek
     u 0..1, v 0..1, dus de uv's zijn gewoon (u, v), in dezelfde richting als het voorvlak van BoxGeometry. */
  function lidGeometry(fc, fw, fh, t) {
    let pts = fc.map(p => new T2.Vector2(p[0], p[1]));
    if (T2.ShapeUtils.isClockWise(pts)) pts = pts.reverse(); /* tegen de klok in, v naar boven */
    let tris;
    try { tris = T2.ShapeUtils.triangulateShape(pts, []); } catch (e) { return null; }
    if (!tris || !tris.length) return null;
    const n = pts.length, z0 = t / 2, P = [], U = [], N = [], I = [];
    const X = p => (p.x - .5) * fw, Y = p => p.y * fh;
    for (const p of pts) { P.push(X(p), Y(p), z0); U.push(p.x, p.y); N.push(0, 0, 1); }
    for (const p of pts) { P.push(X(p), Y(p), -z0); U.push(p.x, p.y); N.push(0, 0, -1); }
    const ccw = tr => { const [a, b, c] = tr, A = pts[a], B = pts[b], C = pts[c]; return (B.x - A.x) * (C.y - A.y) - (C.x - A.x) * (B.y - A.y) >= 0 ? [a, b, c] : [a, c, b]; };
    for (const tr of tris) { const [a, b, c] = ccw(tr); I.push(a, b, c); }
    const nF = I.length;
    for (const tr of tris) { const [a, b, c] = ccw(tr); I.push(n + a, n + c, n + b); }
    const nB = I.length - nF;
    /* rand: per omtrekzijde een quad met de normaal naar buiten (rechts van de looprichting) */
    let base = 2 * n;
    for (let i = 0; i < n; i++) {
      const p = pts[i], q = pts[(i + 1) % n];
      const ex = X(q) - X(p), ey = Y(q) - Y(p), L = Math.hypot(ex, ey) || 1, nx = ey / L, ny = -ex / L;
      P.push(X(p), Y(p), z0, X(q), Y(q), z0, X(q), Y(q), -z0, X(p), Y(p), -z0);
      for (let k = 0; k < 4; k++) { N.push(nx, ny, 0); U.push(0, 0); }
      I.push(base, base + 2, base + 1, base, base + 3, base + 2); base += 4;
    }
    const g = new T2.BufferGeometry();
    g.setAttribute('position', new T2.Float32BufferAttribute(P, 3));
    g.setAttribute('normal', new T2.Float32BufferAttribute(N, 3));
    g.setAttribute('uv', new T2.Float32BufferAttribute(U, 2));
    g.setIndex(I);
    g.addGroup(0, nF, 0); g.addGroup(nF, nB, 1); g.addGroup(nF + nB, I.length - nF - nB, 2);
    /* omtrekpunten (voor en achter) voor realProjectedBox: de echte silhouet, niet het omhullende kader
       (een boogvormig deksel reikt in de hoeken van dat kader niet tot boven) */
    g.userData.hull = [];
    for (const p of pts) g.userData.hull.push(new T2.Vector3(X(p), Y(p), z0), new T2.Vector3(X(p), Y(p), -z0));
    return g;
  }
  /* binnen/buiten-masker van de omtrek, in dezelfde rij-volgorde als de dekseltextuur (zie makeTex):
     de achterkant gebruikt het om te weten waar het gespiegelde beeld nog op het deksel valt */
  function lidMaskTex(fc, bm) {
    const N = 256, c = document.createElement('canvas'); c.width = c.height = N;
    const x = c.getContext('2d'); x.fillStyle = '#000'; x.fillRect(0, 0, N, N); x.fillStyle = '#fff';
    x.beginPath(); fc.forEach((p, i) => { const px = p[0] * N, py = (1 - p[1]) * N; if (i) x.lineTo(px, py); else x.moveTo(px, py); }); x.closePath(); x.fill();
    const t = new T2.CanvasTexture(c); t.flipY = !bm; t.needsUpdate = true; return t;
  }
  function buildLid(Fl, pose, w, h, d, bm) {
    const fh = pose.fe * (h / pose.h), t = Math.max(w, h + fh) * .009;
    const geo = lidGeometry(pose.fc, w, fh, t);
    if (!geo) return null;
    const tex = makeTex(Fl.img, bm), mask = lidMaskTex(pose.fc, bm);
    const front = physMat(tex);
    /* achterkant: dezelfde dekselprent, van achteren leesbaar (u gespiegeld bemonsterd, zoals de achterkant
       van de packs). Waar het gespiegelde beeld net buiten de (niet-symmetrische) omtrek valt, de uitgelijnde
       kleur, zodat er nooit achtergrond van de foto op het deksel komt. */
    const back = physMat(tex);
    back.onBeforeCompile = sh => {
      sh.uniforms.lidMask = { value: mask };
      sh.fragmentShader = 'uniform sampler2D lidMask;\n' + sh.fragmentShader.replace('#include <map_fragment>', '#ifdef USE_MAP\n  vec2 mUv = vec2( 1.0 - vUv.x, vUv.y );\n  vec4 cA = texture2D( map, vUv );\n  vec4 cB = texture2D( map, mUv );\n  diffuseColor.rgb *= mix( cA.rgb, cB.rgb, step( 0.5, texture2D( lidMask, mUv ).r ) );\n#endif');
    };
    back.customProgramCacheKey = () => 'cw-lid-back';
    back.userData.extraTex = mask;
    const edge = physMat(null, rgbHex(mixColor(Fl.avg, .55)));
    const lid = new T2.Mesh(geo, [front, back, edge]);
    /* scharnier = bovenste achterrand van het lichaam; kantelen rond dat scharnier (fa > 0: naar achter) */
    lid.position.set(0, h / 2, -d / 2 + t / 2);
    lid.rotation.x = -(pose.fa || 0);
    lid.userData.hull = geo.userData.hull;
    return lid;
  }

  /* ================= packbouwer: kussen-mesh voor frontale foto's, zelfde stijl als eerder,
     maar met een scherper en rechthoekig masker zonder witte rand. ================= */
  /* kussen-mesh voor een pack. De bolling volgt de ECHTE omtrek van het pakje (alfamasker van de
     cutout): op de rand is z = 0, dus voor- en achterkant (dezelfde mesh, 180 graden gedraaid) sluiten
     daar naadloos op elkaar aan. Vroeger liep de bolling over de hele rechthoek, waardoor op de
     gekartelde rand van het pakje nog een spleet tussen voor en achter zat (zichtbaar bij draaien). */
  function packMaskField(img, asp) {
    try {
      const MW = 96, MH = Math.max(32, Math.round(MW / asp));
      const c = document.createElement('canvas'); c.width = MW; c.height = MH;
      const x = c.getContext('2d', { willReadFrequently: true }); x.drawImage(img, 0, 0, MW, MH);
      const a = x.getImageData(0, 0, MW, MH).data, INF = 1e9, d = new Float32Array(MW * MH);
      /* buiten = enkel wat via doorzichtige pixels met de rand verbonden is (floodfill vanaf de rand).
         Halfdoorzichtige vlekjes BINNEN het pakje (rembg op glanzend wit) tellen zo als binnen: anders
         gaf het afstandsveld daar deukjes in de bolling. */
      const out = new Uint8Array(MW * MH), st = [];
      const push = (k, y) => { const i = y * MW + k; if (k < 0 || y < 0 || k >= MW || y >= MH || out[i] || a[i * 4 + 3] > 128) return; out[i] = 1; st.push(i); };
      for (let k = 0; k < MW; k++) { push(k, 0); push(k, MH - 1); }
      for (let y = 0; y < MH; y++) { push(0, y); push(MW - 1, y); }
      while (st.length) { const i = st.pop(), k = i % MW, y = (i / MW) | 0; push(k + 1, y); push(k - 1, y); push(k, y + 1); push(k, y - 1); }
      for (let i = 0; i < MW * MH; i++) d[i] = out[i] ? 0 : INF;
      /* afstand tot de rand (chamfer 3-4, twee passes), in maskerpixels */
      const at = (cx, cy) => (cx < 0 || cy < 0 || cx >= MW || cy >= MH) ? 0 : d[cy * MW + cx];
      for (let y = 0; y < MH; y++) for (let k = 0; k < MW; k++) { const i = y * MW + k; if (!d[i]) continue;
        d[i] = Math.min(d[i], at(k - 1, y) + 3, at(k, y - 1) + 3, at(k - 1, y - 1) + 4, at(k + 1, y - 1) + 4); }
      for (let y = MH - 1; y >= 0; y--) for (let k = MW - 1; k >= 0; k--) { const i = y * MW + k; if (!d[i]) continue;
        d[i] = Math.min(d[i], at(k + 1, y) + 3, at(k, y + 1) + 3, at(k + 1, y + 1) + 4, at(k - 1, y + 1) + 4); }
      for (let i = 0; i < d.length; i++) d[i] = d[i] / 3 / MW; /* -> fractie van de packbreedte */
      return { d, MW, MH };
    } catch (e) { return null; }
  }
  /* bulge: bolling als fractie van de kortste zijde. Pack = .085 (standaard); platte vormen (tin, blister,
     accessoire, playmat, ...) krijgen een kleinere waarde via product.bulge (auto3d, zie app.js M3D). */
  function packGeo(w, h, field, mirror, bulge) {
    const sx = QL.tier === 'low' ? 48 : 72, sy = QL.tier === 'low' ? 72 : 108;
    const g = new T2.PlaneGeometry(w, h, sx, sy); const pos = g.attributes.position; const depth = Math.min(w, h) * (bulge > 0 ? bulge : .085);
    const R = .2; /* breedte van de afgeronde rand, als fractie van de packbreedte */
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i), y = pos.getY(i);
      let z;
      if (field) {
        /* bilineair het afstandsveld bemonsteren (beeld-assenstelsel: y naar onder) */
        /* begrensd: x/w + .5 kan door afronding net onder 0 vallen (index -1 -> NaN in de geometrie) */
        const fx = clamp(((mirror ? -x : x) / w + .5) * (field.MW - 1), 0, field.MW - 1), fy = clamp((.5 - y / h) * (field.MH - 1), 0, field.MH - 1);
        const x0 = Math.floor(fx), y0 = Math.floor(fy), x1 = Math.min(field.MW - 1, x0 + 1), y1 = Math.min(field.MH - 1, y0 + 1), ax = fx - x0, ay = fy - y0;
        const D = (cx, cy) => field.d[cy * field.MW + cx];
        const dd = (D(x0, y0) * (1 - ax) + D(x1, y0) * ax) * (1 - ay) + (D(x0, y1) * (1 - ax) + D(x1, y1) * ax) * ay;
        /* 2 maskerpixels marge: de alfarand van de textuur ligt fijner dan dit raster, dus op de
           zichtbare rand moet z echt 0 zijn (anders blijft er een spleet tussen voor en achter) */
        const k = Math.min(1, Math.max(0, (dd - 2 / field.MW) / R));
        z = depth * Math.sqrt(1 - (1 - k) * (1 - k)); /* ronde rand: loodrecht op de omtrek, vlak in het midden */
      } else {
        const u = x / (w / 2), v = y / (h / 2);
        z = depth * Math.pow(Math.max(0, 1 - u * u), .5) * Math.pow(Math.max(0, 1 - Math.pow(Math.abs(v), 6)), .7);
      }
      pos.setZ(i, z);
    }
    /* achterkant: de mesh wordt 180 graden gedraaid, dus u spiegelen; zo valt het alfamasker (en de
       rand met z = 0) op exact dezelfde wereldplek als bij de voorkant en sluiten ze naadloos */
    if (mirror) { const uv = g.attributes.uv; for (let i = 0; i < uv.count; i++) uv.setX(i, 1 - uv.getX(i)); uv.needsUpdate = true; }
    g.computeVertexNormals(); return g;
  }

  /* het masker komt niet meer uit een rechthoekige kleurkey (gaf een witte rand): de pack-pillow
     gebruikt de vooraf berekende cutout (make_cutouts.py) rechtstreeks als textuur, alfa en al.
     Laadt via <img> + decode(), niet fetch(): fetch() ondersteunt geen file:// (de mockup draait
     ook rechtstreeks vanaf schijf), een gewone afbeelding wel. */
  async function loadCutoutBitmap(handle) {
    /* onder file:// staan enkel de gecureerde packs in embed-file.js; de andere producten met een 3D-model
       (auto3d) hebben elk een eigen klein embed-script (lazyCutData), anders weigert WebGL de textuur */
    const live = global.CW_RS_3D_LIVE && global.CW_RS_3D_LIVE[handle];
    const liveUrl = live && live.data && live.urls && live.urls.c; /* automatische 3D: de nieuwe uitsnede gaat voor op wat ingebakken is */
    let emb = liveUrl ? null : packCutData()[handle];
    if (!emb && global.location && global.location.protocol === 'file:') emb = await lazyCutData(handle);
    const url = liveUrl || emb || ('https://cardixai-production.up.railway.app/cw-rs/cw-rs-test-c-' + handle + '.webp');
    const im = new Image(); im.decoding = 'async';
    /* cross-origin bron (bv. de Shopify-CDN op de testpagina, 6 okt): zonder crossOrigin weigert WebGL het beeld als
       textuur ("contains cross-origin data"). Enkel voor http(s) op een andere origin; onder file:// en lokaal niet. */
    try { if (/^(https?:)?\/\//i.test(url) && new URL(url, location.href).origin !== location.origin) im.crossOrigin = 'anonymous'; } catch (e) {} /* ook //cdn-adressen zonder schema (asset_url) */
    im.src = url;
    await im.decode();
    /* geen createImageBitmap() hier: onder file:// geeft dat een cross-origin-tainted bitmap die
       WebGL weigert te uploaden, ook al komt het bestand uit dezelfde map. Het <img>-element zelf
       mag wel rechtstreeks als textuurbron (zelfde pad dat de vlakke foto ook al gebruikt). */
    return { img: im, w: im.naturalWidth, h: im.naturalHeight, isBitmap: false };
  }
  /* de pack-pillow-geometrie wordt gebouwd op de VOLLEDIGE afbeeldingsverhouding (packGeo(w,h)
     hieronder), maar de cutout zelf heeft bijna altijd een rand doorzichtige marge rond het
     product (make_cutouts.py snijdt niet pixel-strak) -- een alphaTest-materiaal toont dus enkel
     een KLEINER gebied dan het vlak zelf. Zonder correctie denkt de closed-loop solve (die de
     geometrie als silhouet aanneemt) dat het product groter is dan wat er werkelijk zichtbaar is,
     en komt de 3D meetbaar te klein/verschoven te staan t.o.v. de platte foto (die zijn eigen
     alfa-inhoudskader al wel gebruikt, zie metaOf() in app.js). Dit meet diezelfde inhoudskader
     1x, lokaal (downsampled canvas, geen netwerk nodig: de afbeelding is al gedecodeerd), en zet
     'm om naar lokale meshcoordinaten zodat realProjectedBox() de ECHTE zichtbare rand gebruikt
     i.p.v. de volle plaat-rand. */
  function alphaContentBBox(imgEl) {
    const dflt = { x0: 0, y0: 0, x1: 1, y1: 1 };
    try {
      const Z = 128;
      const s = Math.min(Z / imgEl.naturalWidth, Z / imgEl.naturalHeight);
      const dw = Math.max(1, Math.round(imgEl.naturalWidth * s)), dh = Math.max(1, Math.round(imgEl.naturalHeight * s));
      const c = document.createElement('canvas'); c.width = dw; c.height = dh;
      const ctx = c.getContext('2d', { willReadFrequently: true }); ctx.drawImage(imgEl, 0, 0, dw, dh);
      const d = ctx.getImageData(0, 0, dw, dh).data;
      let minX = dw, maxX = -1, minY = dh, maxY = -1;
      for (let y = 0; y < dh; y++) for (let x = 0; x < dw; x++) { if (d[(y * dw + x) * 4 + 3] > 128) { if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y; } }
      if (maxX < 0) return dflt;
      return { x0: minX / dw, y0: minY / dh, x1: (maxX + 1) / dw, y1: (maxY + 1) / dh };
    } catch (e) { return dflt; }
  }
  async function buildPackProduct(product) {
    let r;
    try { r = await loadCutoutBitmap(product.h); } catch (e) { return null; }
    const tex = makeTex(r.img, r.isBitmap);
    const asp = r.w / r.h; let h = 1.95, w = h * asp; if (w > 2.15) { w = 2.15; h = w / asp; }
    const fld = packMaskField(r.img, asp);
    const bulge = product.bulge > 0 ? product.bulge : .085;
    const g = new T2.Group(); const geo = packGeo(w, h, fld, false, bulge), geoBack = packGeo(w, h, fld, true, bulge);
    /* inhoudskader (0..1, beeld-assenstelsel Y-naar-onder) -> lokale meshcoordinaten (Y-naar-boven,
       gecentreerd): zelfde voor front/gloss/back (identieke geo + textuur), realProjectedBox past
       elke mesh's eigen matrix (incl. back.rotation.y = PI) er nog overheen toe. */
    const cb = product.cbb ? { x0: product.cbb[0], y0: product.cbb[1], x1: product.cbb[2], y1: product.cbb[3] } : alphaContentBBox(r.img);
    const contentLocal = { minX: (cb.x0 - .5) * w, maxX: (cb.x1 - .5) * w, minY: (.5 - cb.y1) * h, maxY: (.5 - cb.y0) * h };
    const front = new T2.Mesh(geo, new T2.MeshLambertMaterial({ map: tex, alphaTest: .5 })); front.material.alphaToCoverage = true; front.userData.contentLocal = contentLocal; g.add(front);
    const gloss = new T2.Mesh(geo, new T2.MeshPhysicalMaterial({ color: 0x000000, map: tex, roughness: .5, metalness: 0, clearcoat: .6, clearcoatRoughness: .35, envMap: env, envMapIntensity: .32, transparent: true, blending: T2.AdditiveBlending, depthWrite: false }));
    gloss.position.z = .0015; gloss.renderOrder = 2; gloss.userData.isEffect = true; g.add(gloss);
    const sweepMat2 = new T2.ShaderMaterial({
      uniforms: { map: { value: tex }, prog: { value: -3 } }, vertexShader: VS,
      fragmentShader: 'uniform sampler2D map; uniform float prog; varying vec2 vUv; void main(){ float a = texture2D(map, vUv).a; float d = (vUv.x*0.8 + vUv.y*0.5) - prog; float b = exp(-d*d*60.0)*0.2 + exp(-d*d*800.0)*0.24; gl_FragColor = vec4(vec3(1.0,0.96,0.88), b*a); }',
      transparent: true, depthWrite: false, blending: T2.AdditiveBlending
    });
    const sweepMesh = new T2.Mesh(geo, sweepMat2); sweepMesh.position.z = .003; sweepMesh.renderOrder = 3; sweepMesh.userData.isEffect = true; if (QL.fx) g.add(sweepMesh);
    /* achterkant: dezelfde foto als de voorkant, van achteren gewoon leesbaar (niet gespiegeld), met een
       lichte lila tint zodat je ziet dat het de achterkant is. De VORM (alfa) komt van de gespiegelde
       uv's van geoBack, zodat de rand exact op die van de voorkant valt (geen spleet); de KLEUR wordt
       met de omgekeerde u bemonsterd zodat beeld en tekst juist staan. Waar dat ongespiegelde beeld
       net buiten zijn eigen vorm valt (rand van een niet-symmetrisch pakje), de uitgelijnde kleur. */
    const backMat = new T2.MeshStandardMaterial({ map: tex, color: 0xe4dcf2, roughness: .4, metalness: .12, envMap: env, envMapIntensity: .35, alphaTest: .5 });
    backMat.onBeforeCompile = sh => { sh.fragmentShader = sh.fragmentShader.replace('#include <map_fragment>', '#ifdef USE_MAP\n  vec4 cA = texture2D( map, vUv );\n  vec4 cB = texture2D( map, vec2( 1.0 - vUv.x, vUv.y ) );\n  diffuseColor.a *= cA.a;\n  diffuseColor.rgb *= mix( cA.rgb, cB.rgb, step( 0.5, cB.a ) );\n#endif'); };
    backMat.customProgramCacheKey = () => 'cw-pack-back-art';
    const back = new T2.Mesh(geoBack, backMat); back.rotation.y = Math.PI; back.userData.isEffect = true; g.add(back);
    /* lichte heldpose (~15 graden) ook voor packs: een pure frontale 0-stand oogt vlak/dood. */
    return { type: 'pack', group: g, w, h, d: Math.min(w, h) * 2 * bulge, heroYaw: -.26, sweep: QL.fx ? { material: sweepMat2 } : null, sweepT: -.6 };
  }

  /* ================= frontdoos (auto3d niveau 1, 6 okt) =================
     Doos met enkel een frontale foto: een echte BoxGeometry, de uitsnede als voorvlak (bijgesneden tot het
     productkader, geen uitrekking: de doos krijgt exact de verhouding van dat kader), diepte = vaste
     verhouding per doossoort (product.fbox.dw = diepte/breedte, gemeten op de 21 opgeloste dozen, zie
     auto3d_depth.py). Sinds auto3d2 (zie fboxFaceTex hieronder): foto als materiaal van voor- en achtervlak,
     randstroken van de foto op de zijvlakken, randkleur (product.fbox.edge, cutmeta.py) waar de uitsnede
     doorzichtig is. shadeTex (effen verloop) wordt niet meer gebruikt. */
  function shadeTex(rgb, vertical) {
    const c = document.createElement('canvas'); c.width = vertical ? 4 : 64; c.height = vertical ? 64 : 4;
    const x = c.getContext('2d'), g = vertical ? x.createLinearGradient(0, 0, 0, 64) : x.createLinearGradient(0, 0, 64, 0);
    const col = k => 'rgb(' + rgb.map(v => clamp(Math.round(v * k), 0, 255)).join(',') + ')';
    g.addColorStop(0, col(1.1)); g.addColorStop(1, col(.84));
    x.fillStyle = g; x.fillRect(0, 0, c.width, c.height);
    const t = new T2.CanvasTexture(c); t.encoding = T2.sRGBEncoding; return t;
  }
  /* Frontdoos herwerkt (auto3d2, 6 okt; eigenaar: "fotovlak staat scheef t.o.v. de beige doos" en "vanuit een
     andere hoek is de foto weg"). Oorzaak: de foto was een LOS vlak (PlaneGeometry met alphaTest, FrontSide)
     over het volle productkader, terwijl de doos enkel achter de "kern" van de uitsnede stond (cutmeta core,
     bij een open display enkel de smalle middenstrook). Foto en doos hadden dus een andere maat en een ander
     midden, en van opzij of achteren zag je alleen de effen doos (het losse vlak wordt van achteren geculled).
     Nu: EEN BoxGeometry op het volle productkader, de foto is het MATERIAAL van het voorvlak (+z) en, net als bij
     de volwaardige dozen, ook van het achtervlak (-z, van achteren leesbaar). Doorzichtige pixels van de
     uitsnede (afgeronde hoeken) krijgen de randkleur, zodat er nooit een gat is. Zij-, boven- en ondervlak:
     de dominante kleur van dat randgebied van de foto, effen met een licht verloop (edgeColorOf/shadeFaceTex). */
  function fboxFaceTex(img, W, H, sx, sy, sw, sh, tw, th, edge, flipX, flipY, shade) {
    const c = document.createElement('canvas'); c.width = Math.max(2, Math.round(tw)); c.height = Math.max(2, Math.round(th));
    const x = c.getContext('2d');
    x.fillStyle = 'rgb(' + edge.join(',') + ')'; x.fillRect(0, 0, c.width, c.height);
    x.save();
    x.translate(flipX ? c.width : 0, flipY ? c.height : 0); x.scale(flipX ? -1 : 1, flipY ? -1 : 1);
    try { x.drawImage(img, sx * W, sy * H, Math.max(1, sw * W), Math.max(1, sh * H), 0, 0, c.width, c.height); } catch (e) {}
    x.restore();
    if (shade) {
      /* lichte schaduw naar achter: 0 aan het voorvlak, ~20% donkerder aan de achterrand */
      const g = shade === 'h' ? x.createLinearGradient(flipX ? c.width : 0, 0, flipX ? 0 : c.width, 0) : x.createLinearGradient(0, flipY ? c.height : 0, 0, flipY ? 0 : c.height);
      g.addColorStop(0, 'rgba(0,0,0,0.04)'); g.addColorStop(1, 'rgba(0,0,0,0.2)');
      x.fillStyle = g; x.fillRect(0, 0, c.width, c.height);
    }
    const t = new T2.CanvasTexture(c); t.encoding = T2.sRGBEncoding; t.anisotropy = Math.min(maxAniso, QL.aniso);
    t.minFilter = T2.LinearMipmapLinearFilter; t.magFilter = T2.LinearFilter; t.generateMipmaps = true;
    return t;
  }
  /* mediaan-kleur van een gebied van de uitsnede (enkel pixels met alfa > 200), terugval = randkleur uit cutmeta */
  function edgeColorOf(img, W, H, sx, sy, sw, sh, dflt) {
    try {
      const N = 48, c = document.createElement('canvas'); c.width = N; c.height = N;
      const x = c.getContext('2d', { willReadFrequently: true });
      x.drawImage(img, sx * W, sy * H, Math.max(1, sw * W), Math.max(1, sh * H), 0, 0, N, N);
      const d = x.getImageData(0, 0, N, N).data, ch = [[], [], []];
      for (let i = 0; i < d.length; i += 4) if (d[i + 3] > 200) { ch[0].push(d[i]); ch[1].push(d[i + 1]); ch[2].push(d[i + 2]); }
      if (ch[0].length < 20) return dflt;
      return ch.map(a => { a.sort((p, q) => p - q); return a[a.length >> 1]; });
    } catch (e) { return dflt; }
  }
  /* effen vlak in kleur rgb met licht verloop: lichtst aan de voorrand (front = de kant van u 0 bij 'h' en flip,
     v 0 bij 'v' en flip), ~16% donkerder naar achter */
  function shadeFaceTex(rgb, dir, flip) {
    const c = document.createElement('canvas'); c.width = dir === 'h' ? 64 : 4; c.height = dir === 'h' ? 4 : 64;
    const x = c.getContext('2d');
    const g = dir === 'h' ? x.createLinearGradient(flip ? 0 : 64, 0, flip ? 64 : 0, 0) : x.createLinearGradient(0, flip ? 64 : 0, 0, flip ? 0 : 64);
    const col = k => 'rgb(' + rgb.map(v => clamp(Math.round(v * k), 0, 255)).join(',') + ')';
    g.addColorStop(0, col(1.02)); g.addColorStop(1, col(.84));
    x.fillStyle = g; x.fillRect(0, 0, c.width, c.height);
    const t = new T2.CanvasTexture(c); t.encoding = T2.sRGBEncoding; return t;
  }
  /* 18 okt (Andrea): kleurvariant. Variantfoto (volledige productfoto op een lichte achtergrond) bijsnijden tot het product:
     bbox van de pixels die van de hoekkleur verschillen, zodat de foto dezelfde verhouding krijgt als de uitsnede. */
  function variantCrop(im) {
    try {
      const W = im.naturalWidth, H = im.naturalHeight, c = document.createElement('canvas'); c.width = W; c.height = H;
      const x = c.getContext('2d', { willReadFrequently: true }); x.drawImage(im, 0, 0);
      const d = x.getImageData(0, 0, W, H).data, bg = [0, 0, 0];
      [[2, 2], [W - 3, 2], [2, H - 3], [W - 3, H - 3]].forEach(q => { const o = (q[1] * W + q[0]) * 4; for (let k = 0; k < 3; k++) bg[k] += d[o + k] / 4; });
      let x0 = W, y0 = H, x1 = -1, y1 = -1;
      for (let j = 0; j < H; j += 2) for (let i = 0; i < W; i += 2) {
        const o = (j * W + i) * 4;
        if (Math.abs(d[o] - bg[0]) + Math.abs(d[o + 1] - bg[1]) + Math.abs(d[o + 2] - bg[2]) > 60) { if (i < x0) x0 = i; if (i > x1) x1 = i; if (j < y0) y0 = j; if (j > y1) y1 = j; }
      }
      if (x1 < 0 || (x1 - x0) * (y1 - y0) < W * H * .15) { x0 = 0; y0 = 0; x1 = W - 1; y1 = H - 1; }
      const cw = x1 - x0 + 1, ch = y1 - y0 + 1, o2 = document.createElement('canvas'); o2.width = cw; o2.height = ch;
      o2.getContext('2d').drawImage(c, x0, y0, cw, ch, 0, 0, cw, ch); return o2;
    } catch (e) { return null; }
  }
  async function buildFrontBoxProduct(product) {
    let r;
    try { r = await loadCutoutBitmap(product.h); } catch (e) { return null; }
    const fb = product.fbox || {};
    const bb = fb.bb && fb.bb.length === 4 ? fb.bb : [0, 0, 1, 1];
    const bw = Math.max(.05, bb[2] - bb[0]), bh = Math.max(.05, bb[3] - bb[1]);
    const asp = (r.w * bw) / (r.h * bh);
    let h = 1.9, w = h * asp; if (w > 2.6) { w = 2.6; h = w / asp; }
    const edge = fb.edge && fb.edge.length === 3 ? fb.edge : [120, 100, 150];
    const IW = r.w, IH = r.h;
    /* Twee vormen. (1) Gewone frontale doos: de doos is het volle productkader. (2) Open display recht van voren
       (deksel/kop steekt boven de doos uit, cutmeta core: het dichte deel beslaat minder dan 60% van het kader en
       loopt tot onderaan): de doos is enkel de kern, het volle beeld staat als STAAND PANEEL tegen de achterkant
       van die doos (zoals het deksel van een echte display), zodat het uitstekende deel geen randkleurvlak wordt.
       In beide gevallen zit de foto op het voorvlak zelf (zelfde maat, zelfde midden, draait mee). */
    let core = fb.core && fb.core.length === 4 ? fb.core : bb;
    const coreFrac = ((core[2] - core[0]) * (core[3] - core[1])) / (bw * bh);
    const open = coreFrac < .6 && core[3] >= bb[1] + .85 * bh && (core[3] - core[1]) / bh >= .15 && (core[2] - core[0]) / bw >= .5;
    if (!open) core = bb;
    const cbw = core[2] - core[0], cbh = core[3] - core[1];
    const cw = cbw / bw * w, ch = cbh / bh * h;
    const cx = (((core[0] + core[2]) - (bb[0] + bb[2])) / 2) / bw * w, cy = -(((core[1] + core[3]) - (bb[1] + bb[3])) / 2) / bh * h;
    const mat = t => QL.phys ? new T2.MeshPhysicalMaterial({ map: t, roughness: .45, metalness: .05, envMap: env, envMapIntensity: .16, clearcoat: .14, clearcoatRoughness: .32 })
      : new T2.MeshStandardMaterial({ map: t, roughness: .42, metalness: .05, envMap: env, envMapIntensity: .16 });
    /* fill (optioneel, [r,g,b]): achtergrond onder doorzichtige delen van de uitsnede (acryl: lichte kleur i.p.v. de randkleur) */
    const fill = fb.fill && fb.fill.length === 3 ? fb.fill : edge;
    /* een doos (blok) voor gebied rc van de uitsnede (fracties), breedte bw2 x hoogte bh2 in scene-eenheden, diepte dd */
    const block = (rc, bw2, bh2, dd) => {
      const rw = rc[2] - rc[0], rh = rc[3] - rc[1];
      /* voorvlak: dat gebied van de uitsnede op de vulkleur, max 2048 px op de lange zijde */
      const pw = IW * rw, ph = IH * rh, k = Math.min(1, 2048 * QL.tex / Math.max(pw, ph));
      const frontT = fboxFaceTex(r.img, IW, IH, rc[0], rc[1], rw, rh, pw * k, ph * k, fill, false, false, null);
      /* vlakken die niet op de foto staan (zij-, boven-, ondervlak): de DOMINANTE kleur van dat randgebied van de
         foto (band van 6% langs die rand, enkel dichte pixels, mediaan per kanaal) als effen vlak met een licht
         verloop naar achter. Geen uitgerekte randstrook meer: die gaf strepen (eigenaar 6 okt). */
      /* side (optioneel, [r,g,b], rest12 7 okt): vaste kleur voor zij-, boven- en ondervlak i.p.v. de randkleur van de
         foto (bv. de Kluis: een ontwerp met packs langs de rand, de eigenaar wil een rustige donkerpaarse diepte) */
      const band = (sx, sy, sw, sh) => (fb.side && fb.side.length === 3) ? fb.side : edgeColorOf(r.img, IW, IH, sx, sy, sw, sh, edge);
      const bx = rw * .06, by = rh * .06;
      const tR = shadeFaceTex(band(rc[2] - bx, rc[1], bx, rh), 'h', true);
      const tL = shadeFaceTex(band(rc[0], rc[1], bx, rh), 'h', false);
      const tT = shadeFaceTex(band(rc[0], rc[1], rw, by), 'v', true);
      const tB = shadeFaceTex(band(rc[0], rc[3] - by, rw, by), 'v', false);
      /* BoxGeometry-groepsvolgorde [+x, -x, +y, -y, +z, -z]; voor- en achterkant dezelfde foto (zoals buildBoxProduct) */
      const frontM = mat(frontT);
      return new T2.Mesh(new T2.BoxGeometry(bw2, bh2, dd), [mat(tR), mat(tL), mat(tT), mat(tB), frontM, frontM]);
    };
    /* diepte: vaste verhouding per soort t.o.v. de breedte, begrensd zodat een smal hoog front geen balk wordt */
    const d = clamp((fb.dw > 0 ? fb.dw : .45) * cw, .06, Math.max(cw, ch) * .9);
    const toScene = rc => ({ w: (rc[2] - rc[0]) / bw * w, h: (rc[3] - rc[1]) / bh * h,
      x: (((rc[0] + rc[2]) - (bb[0] + bb[2])) / 2) / bw * w, y: -(((rc[1] + rc[3]) - (bb[1] + bb[3])) / 2) / bh * h });
    if (Array.isArray(fb.parts) && fb.parts.length) {
      /* meerdere producten op een foto (bv. een bundel van 3 houders): elk deel een eigen blok op zijn plek uit de
         foto, met een eigen diepte (dw x eigen breedte) en achter elkaar (part.z in diepte-eenheden, 0 = vooraan) */
      const group = new T2.Group(); let zmin = 0, dmax = 0;
      for (const pt of fb.parts) {
        const S2 = toScene(pt.bb), dd = clamp((fb.dw > 0 ? fb.dw : .45) * S2.w, .04, Math.max(S2.w, S2.h) * .9);
        const m = block(pt.bb, S2.w, S2.h, dd); const z = -(pt.z || 0) * dd * 1.6;
        m.position.set(S2.x, S2.y, z); group.add(m); zmin = Math.min(zmin, z - dd / 2); dmax = Math.max(dmax, dd);
      }
      return { type: 'box', group, w, h, d: dmax / 2 - zmin, heroYaw: -.34, heroPitch: .1, sweep: null, sweepT: -.6 };
    }
    const body = block(core, cw, ch, d); body.position.set(cx, cy, 0);
    const group = new T2.Group(); group.add(body);
    /* 18 okt (Andrea): recolor(url) zet voor- en achtervlak en de zijkleuren van de doos uit een variantfoto (zelfde pijplijn als block():
       fboxFaceTex + edgeColorOf/shadeFaceTex); null = terug naar de uitsnede. Eerst laden en bijsnijden, dan in 1 keer wisselen (geen flikker). */
    let rcKey = null, rcTok = 0, vMats = null; const origMats = body.material;
    const freeV = () => { if (vMats) { vMats.forEach(m => { if (m.map) m.map.dispose(); m.dispose(); }); vMats = null; } };
    const recolor = async url => {
      url = url || null; const my = ++rcTok; if (url === rcKey) return true; let mats = origMats; /* tok eerst: een lopende laad van een andere kleur vervalt */
      if (url) {
        const im = new Image(); im.decoding = 'async';
        try { if (/^(https?:)?\/\//i.test(url) && new URL(url, location.href).origin !== location.origin) im.crossOrigin = 'anonymous'; } catch (e) {}
        im.src = url; try { await im.decode(); } catch (e) { return false; }
        if (my !== rcTok) return false;
        const cc = variantCrop(im); if (!cc) return false;
        const CW = cc.width, CH = cc.height, rw = core[2] - core[0], rh = core[3] - core[1], pw = CW * rw, ph = CH * rh, k = Math.min(1, 2048 * QL.tex / Math.max(pw, ph));
        const fm = mat(fboxFaceTex(cc, CW, CH, core[0], core[1], rw, rh, pw * k, ph * k, fill, false, false, null));
        const band = (sx, sy, sw, sh) => (fb.side && fb.side.length === 3) ? fb.side : edgeColorOf(cc, CW, CH, sx, sy, sw, sh, edge);
        const bx = rw * .06, by = rh * .06;
        mats = [mat(shadeFaceTex(band(core[2] - bx, core[1], bx, rh), 'h', true)), mat(shadeFaceTex(band(core[0], core[1], bx, rh), 'h', false)),
          mat(shadeFaceTex(band(core[0], core[1], rw, by), 'v', true)), mat(shadeFaceTex(band(core[0], core[3] - by, rw, by), 'v', false)), fm, fm];
      }
      if (my !== rcTok) return false;
      body.material = mats; freeV(); vMats = url ? mats : null; rcKey = url; return true;
    };
    const vFree = () => { freeV(); origMats.forEach(m => { if (m.map) m.map.dispose(); m.dispose(); }); };
    if (open) {
      /* staand paneel achteraan: het volle beeld met alfa. Voorkant naar +z; achterkant een tweede vlak (180 graden
         gedraaid, u gespiegeld zodat de VORM op dezelfde plek valt) dat de KLEUR met de omgekeerde u leest, zodat
         beeld en tekst van achteren leesbaar zijn (zelfde aanpak als de achterkant van de packs en het deksel). */
      const texP = makeTex(r.img, r.isBitmap); texP.repeat.set(bw, bh); texP.offset.set(bb[0], 1 - bb[3]);
      const pz = -d / 2 - .004;
      const pf = new T2.Mesh(new T2.PlaneGeometry(w, h), new T2.MeshStandardMaterial({ map: texP, roughness: .42, metalness: .05, envMap: env, envMapIntensity: .16, alphaTest: .5 }));
      pf.position.z = pz; group.add(pf);
      const gb = new T2.PlaneGeometry(w, h); const uvb = gb.attributes.uv; for (let i = 0; i < uvb.count; i++) uvb.setX(i, 1 - uvb.getX(i)); uvb.needsUpdate = true;
      const bm = new T2.MeshStandardMaterial({ map: texP, roughness: .42, metalness: .05, envMap: env, envMapIntensity: .16, alphaTest: .5 });
      const su = bb[0] + bb[2];
      bm.onBeforeCompile = sh => { sh.fragmentShader = sh.fragmentShader.replace('#include <map_fragment>', '#ifdef USE_MAP\n  vec4 cA = texture2D( map, vUv );\n  vec4 cB = texture2D( map, vec2( ' + su.toFixed(5) + ' - vUv.x, vUv.y ) );\n  diffuseColor.a *= cA.a;\n  diffuseColor.rgb *= mix( cA.rgb, cB.rgb, step( 0.5, cB.a ) );\n#endif'); };
      bm.customProgramCacheKey = () => 'cw-fbox-panel-back-' + su.toFixed(5);
      const pb = new T2.Mesh(gb, bm); pb.rotation.y = Math.PI; pb.position.z = pz - .002; pb.userData.isEffect = true; group.add(pb);
    }
    const sweepGeo = new T2.BoxGeometry(cw * 1.008, ch * 1.008, d * 1.008);
    const sweepMat2 = new T2.ShaderMaterial({
      uniforms: { prog: { value: -3 }, dimW: { value: cw }, dimH: { value: ch }, dimD: { value: d } },
      vertexShader: 'varying vec3 vP; void main(){ vP = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: 'uniform float prog; uniform float dimW, dimH, dimD; varying vec3 vP; void main(){ float t = vP.x / dimW + vP.y / dimH * 0.6 + vP.z / dimD * 0.3; float d = t - prog; float b = exp(-d*d*22.0) * 0.22 + exp(-d*d*260.0) * 0.26; gl_FragColor = vec4(vec3(1.0, 0.97, 0.9), b); }',
      transparent: true, depthWrite: false, blending: T2.AdditiveBlending
    });
    const sweepMesh = new T2.Mesh(sweepGeo, sweepMat2); sweepMesh.position.set(cx, cy, 0); sweepMesh.renderOrder = 3; sweepMesh.userData.isEffect = true; if (QL.fx) group.add(sweepMesh);
    /* heldpose: licht gedraaid zodat je de diepte meteen ziet (de foto zelf is frontaal) */
    return { type: 'box', group, w, h, d, heroYaw: -.34, heroPitch: .1, sweep: QL.fx ? { material: sweepMat2 } : null, sweepT: -.6, recolor: open ? undefined : recolor, vFree: open ? undefined : vFree };
  }

  /* ================= duo (18 okt): bundel van twee dozen als 1 model =================
     De echte 3D van de losse producten (product.duoP: hun vlakken en pose3d, zoals elk ander), naast elkaar met een kleine
     tussenruimte en elk licht naar het midden gedraaid. De meshes komen rechtstreeks in de groep (met hun eigen positie en
     draaiing), zodat realProjectedBox (mesh.matrix t.o.v. de groep) het echte silhouet meet. Geen niet-uniforme kalibratie:
     de kadrering past het duo in het doel (contain), zodat de dozen nooit uitgerekt worden. */
  async function buildDuoProduct(product) {
    const parts = product.duoP; if (!parts || parts.length !== 2) return null;
    const objs = await Promise.all(parts.map(q => buildBoxProduct(q).catch(() => null)));
    if (!objs[0] || !objs[1]) { objs.forEach(o => { if (o) disposeObj(o); }); return null; }
    const ANG = .26, gap = .07 * objs[0].h;
    const span = o => o.w * Math.cos(ANG) + o.d * Math.sin(ANG);
    const sA = span(objs[0]), sB = span(objs[1]), W = sA + gap + sB;
    const group = new T2.Group();
    objs.forEach((o, i) => {
      const x = i === 0 ? -W / 2 + sA / 2 : W / 2 - sB / 2, ry = i === 0 ? ANG : -ANG;
      [...o.group.children].forEach(c => {
        if (c.userData && c.userData.isEffect) { if (c.geometry) c.geometry.dispose(); if (c.material) c.material.dispose(); o.group.remove(c); return; }
        o.group.remove(c); c.position.x += x; c.rotation.y += ry; group.add(c);
      });
    });
    const d = Math.max(objs[0].w * Math.sin(ANG) + objs[0].d * Math.cos(ANG), objs[1].w * Math.sin(ANG) + objs[1].d * Math.cos(ANG));
    return { type: 'box', group, w: W, h: Math.max(objs[0].h, objs[1].h), d, heroYaw: .1, heroPitch: .08, sweep: null, sweepT: -.6, contain: true, calibDone: true };
  }
  async function loadProduct(product) {
    if (product.mode === 'duo') return buildDuoProduct(product);
    if (product.faces && product.faces.front && (product.faces.side || product.faces.top)) return buildBoxProduct(product);
    if (product.mode === 'fbox') return buildFrontBoxProduct(product);
    if (product.mode === 'pack') return buildPackProduct(product);
    return null;
  }

  /* ?q3ddpr=N (render-pipeline, 18 okt): pixelratio-plafond voor een scherpe render (duo-uitsnede op DPR 3); live nooit gezet */
  const DPR_MAX = (() => { try { const v = +new URLSearchParams(global.location.search).get('q3ddpr'); return v > 0 && v <= 4 ? v : 0; } catch (e) { return 0; } })();
  function pr(w, h) { return Math.max(.75, Math.min(global.devicePixelRatio || 1, DPR_MAX || QL.dpr) * (DPR_MAX ? 1 : S.prScale)); }
  function init() {
    /* THREE/gsap pas hier ophalen: als init() aangeroepen wordt voor een async script klaar is,
       geeft dit gewoon false terug zodat de aanroeper later opnieuw kan proberen, in plaats van
       de module voorgoed leeg te maken. */
    T2 = global.THREE; G = global.gsap;
    if (!T2) return false;
    QL = Object.assign({ tier: detectTier().tier }, Q[detectTier().tier]);
    /* 'high-performance' wekt op laptops met 2 GPU's de zware GPU (batterij, opstartvertraging): enkel op 'high' */
    try { renderer = new T2.WebGLRenderer({ antialias: QL.aa, alpha: true, powerPreference: QL.tier === 'high' ? 'high-performance' : 'default' }); }
    catch (e) { return false; }
    /* productie: geen synchrone shaderfoutcontrole (getProgramInfoLog wacht tot de GPU klaar is met
       compileren, ~16 ms blokkade per nieuw programma); de shaders hier zijn vast en getest */
    renderer.debug.checkShaderErrors = false;
    renderer.outputEncoding = T2.sRGBEncoding; renderer.toneMapping = T2.NoToneMapping; renderer.setClearColor(0x000000, 0);
    maxAniso = renderer.capabilities.getMaxAnisotropy();
    scene = new T2.Scene(); camera = new T2.PerspectiveCamera(28, 1, .1, 60); camera.position.set(0, .08, 6.2);
    const pm = new T2.PMREMGenerator(renderer); env = pm.fromScene(envScene(), .035).texture; pm.dispose();
    scene.add(new T2.AmbientLight(0xffffff, .58));
    const key = new T2.DirectionalLight(0xfff2da, .62); key.position.set(2.6, 3.2, 4.8); scene.add(key);
    const rimPurple = new T2.DirectionalLight(0x7B5EA7, .55); rimPurple.position.set(-4.2, 1.6, -2); scene.add(rimPurple);
    const rimGold = new T2.DirectionalLight(0xE8C840, .4); rimGold.position.set(4.4, 1.2, -2.4); scene.add(rimGold);
    root = new T2.Group(); scene.add(root);
    holder = new T2.Group(); root.add(holder);
    const n = 40, dg = new T2.BufferGeometry(), dp = new Float32Array(n * 3), sp = new Float32Array(n);
    for (let i = 0; i < n; i++) { dp[i * 3] = (Math.random() - .5) * 4.6; dp[i * 3 + 1] = (Math.random() - .5) * 3.2; dp[i * 3 + 2] = (Math.random() - .5) * 2.4 - .4; sp[i] = .03 + Math.random() * .08; }
    dg.setAttribute('position', new T2.BufferAttribute(dp, 3)); dg.userData.sp = sp;
    if (QL.fx) dust = new T2.Points(dg, new T2.PointsMaterial({ size: .026, map: radialTex('rgba(232,200,64,1)', 'rgba(232,200,64,0)'), transparent: true, depthWrite: false, opacity: .5 })); if (dust) scene.add(dust);
    const cv = renderer.domElement; cv.className = 'stage3d-canvas'; cv.setAttribute('aria-hidden', 'true');
    cv.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;opacity:0;transition:none;touch-action:none;';
    if (global.ResizeObserver) ro = new ResizeObserver(() => resize());
    initWorker();
    /* geen eigen window-brede pointermove meer: app.js roept pointer(x,y) enkel aan met coordinaten
       relatief aan de actieve stage (0,0 zodra de muis die stage verlaat), zodat de hover-tilt
       hieronder echt op de stage reageert in plaats van op de hele paginabreedte. */
    return true;
  }
  /* layoutmaat van de stage in CSS px, ZONDER transforms: tijdens het scrollen schaalt parallax() de
     stage (scale < 1); getBoundingClientRect zou die geschaalde maat geven en de kadrering vertekenen. */
  function boxOf(el) { return { width:el.clientWidth, height:el.clientHeight }; }
  function resize() {
    if (!host || !renderer) return; const r = boxOf(host); if (r.width < 10 || r.height < 10) return; S.vh = r.height;
    renderer.setPixelRatio(pr(r.width, r.height)); renderer.setSize(r.width, r.height, false);
    camera.aspect = r.width / r.height; camera.updateProjectionMatrix(); layout();
  }
  /* kadrering: EENMALIG per product (bij show() en bij een echte container-resize), nooit opnieuw
     bij hover/drag/idle.
     Primair pad (cur.targetFrac gezet): een klein reprojectie-solve dat schaal + verschuiving zo
     kiest dat de geprojecteerde heldpose-doos exact de rechthoek vult die app.js opmat uit de platte
     cutout-foto zelf (targetRectFor in app.js, dezelfde (tx,ty,z) als fitPhoto) -- dus geen generieke
     vulbreuk meer, maar de ECHTE foto-afmeting, zodat de kruisfade niet zichtbaar in- of uitzoomt.
     Terugvalpad (geen targetFrac, bv. metaOf nog niet klaar): de oude FILL-vulling (~90%). */
  const FILL = .9;
  /* projecteert de 8 doos-hoekpunten (lokaal, rond het eigen midden) door de vaste camera bij een
     gegeven schaal sc en voor-schaal wereldverschuiving (Tx,Ty); vaste camera + fov komen overeen met
     die van de offline pose-solve (solve_pose.py), dus de solve hieronder blijft fysisch consistent
     met hoe de heldpose zelf bepaald is. */
  function projectedBox(obj, sc, Tx, Ty, canvasW, canvasH) {
    const yaw = obj.heroYaw || 0, pitch = obj.heroPitch || 0;
    const hw = obj.w / 2, hh = obj.h / 2, hd = (obj.d || 0) / 2;
    const cp = Math.cos(pitch), sp = Math.sin(pitch), cy = Math.cos(yaw), sy = Math.sin(yaw);
    const dist = camera.position.z, tanY = Math.tan(T2.MathUtils.degToRad(camera.fov / 2)), tanX = tanY * camera.aspect;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const sx of [-1, 1]) for (const syy of [-1, 1]) for (const sz of [-1, 1]) {
      const lx = sx * hw, ly = syy * hh, lz = sz * hd;
      /* zelfde Euler-volgorde als holder.rotation.set(pitch, yaw, roll) (three.js default 'XYZ'): */
      const y1 = ly * cp - lz * sp, z1 = ly * sp + lz * cp;
      const x2 = lx * cy + z1 * sy, z2 = -lx * sy + z1 * cy;
      const wx = sc * (x2 + Tx), wy = sc * (y1 + Ty), wz = sc * z2;
      const depth = Math.max(.05, dist - wz);
      const ndcX = wx / depth / tanX, ndcY = wy / depth / tanY;
      const px = (.5 + .5 * ndcX) * canvasW, py = (.5 - .5 * ndcY) * canvasH;
      if (px < minX) minX = px; if (px > maxX) maxX = px; if (py < minY) minY = py; if (py > maxY) maxY = py;
    }
    return { minX, maxX, minY, maxY, w: maxX - minX, h: maxY - minY, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
  }
  /* ================= echte-mesh projectie (closed loop) =================
     In tegenstelling tot projectedBox() hierboven (die een IDEALISEERDE doos aanneemt, enkel
     obj.w/h/d) projecteert dit de WERKELIJKE geometrie van elke mesh in obj.group: de eigen lokale
     bounding-box van elke mesh (dus ook een pack-pillow-bulge of een andere afwijking van een
     perfecte doos telt mee zoals hij ECHT rendert), door de mesh's eigen lokale matrix (positie/
     rotatie t.o.v. obj.group, bv. de gespiegelde 'back'-pillow met rotation.y = PI), dan door
     dezelfde virtuele heldpose-transform (yaw/pitch/sc/Tx/Ty) als hierboven. sweep-overlays
     (userData.isEffect, de glans-animatie) tellen niet mee: dat is een voorbijtrekkend lichteffect,
     geen onderdeel van de echte silhouet. Dit is zowel de bron voor de kadrering zelf (solveFrame,
     layout) als voor de eerlijke debug/test-meting (debugFrame): geen aparte "ideale" aanname meer
     die zichzelf gelijk geeft. */
  function meshLocalCorners(mesh) {
    /* userData.hull (deksel van een open booster box): de echte omtrekpunten i.p.v. de 8 kaderhoeken */
    const hull = mesh.userData && mesh.userData.hull;
    if (hull) { mesh.updateMatrix(); return hull.map(p => p.clone().applyMatrix4(mesh.matrix)); }
    const geo = mesh.geometry;
    if (!geo.boundingBox) geo.computeBoundingBox();
    const bb = geo.boundingBox;
    /* userData.contentLocal (zie buildPackProduct): het ALFA-INHOUDSKADER van een cutout-textuur
       ligt bijna altijd binnen de volle plaat-geometrie (transparante marge rond het product). Een
       alphaTest-materiaal toont dus minder dan de geometrie zelf -- zonder deze correctie denkt de
       closed-loop solve dat het product tot de plaatrand reikt, en komt de 3D meetbaar te klein
       t.o.v. de platte foto (die zijn eigen inhoudskader al wel gebruikt). Enkel X/Y vervangen, Z
       (de eventuele pillow-bulge) blijft van de echte geometrie komen. */
    const cl = mesh.userData && mesh.userData.contentLocal;
    const minX = cl ? cl.minX : bb.min.x, maxX = cl ? cl.maxX : bb.max.x;
    const minY = cl ? cl.minY : bb.min.y, maxY = cl ? cl.maxY : bb.max.y;
    mesh.updateMatrix();
    const pts = [];
    for (let i = 0; i < 8; i++) {
      const x = (i & 1) ? maxX : minX, y = (i & 2) ? maxY : minY, z = (i & 4) ? bb.max.z : bb.min.z;
      pts.push(new T2.Vector3(x, y, z).applyMatrix4(mesh.matrix));
    }
    return pts;
  }
  function realProjectedBox(obj, sc, Tx, Ty, canvasW, canvasH) {
    if (!obj.group) return projectedBox(obj, sc, Tx, Ty, canvasW, canvasH);
    const yaw = obj.heroYaw || 0, pitch = obj.heroPitch || 0;
    const cp = Math.cos(pitch), sp = Math.sin(pitch), cy = Math.cos(yaw), sy = Math.sin(yaw);
    const dist = camera.position.z, tanY = Math.tan(T2.MathUtils.degToRad(camera.fov / 2)), tanX = tanY * camera.aspect;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity, any = false;
    /* BELANGRIJK: obj.calib (de empirische pixelcorrectie, zie calibrateOnce) komt hier NIET in --
       enkel de geometrische content-bbox (meshLocalCorners) telt mee. obj.calib vangt een render-tijd
       effect (mipmap/alphaTest-randverzachting bij deze rendergrootte) dat geen geometrisch model kan
       voorspellen; zou solveFrame die calib ALS geometrie behandelen (dus meesolven), dan "ontdekt"
       een volgende solveFrame-aanroep (bv. na een resize) een ANDERE combinatie van root.scale en
       group.scale die het PUUR GEOMETRISCHE model ook laat kloppen, en verdwijnt de empirische
       correctie zelf goeddeels weer (geverifieerd: dat gaf na een 2e layout()-aanroep een render die
       meetbaar te klein terugviel, 164 i.p.v. de gekalibreerde ~169px). solveFrame/realProjectedBox
       blijven dus altijd hetzelfde, stabiele, zuiver geometrische antwoord geven; calibrateOnce past
       zijn bevinding apart toe (layout() hieronder, cur.group.scale) BOVENOP dat antwoord. */
    /* obj.rotXY (enkel open booster boxes): exact de volgorde die holder.rotation.set(pitch, yaw, 0)
       rendert, Rx(pitch)·Ry(yaw), dus eerst yaw en dan pitch. De bestaande producten houden de volgorde
       hieronder (hun kalibratie is daarop afgestemd); bij hun kleine pitch maakt het nauwelijks uit, bij de
       sterk van boven gefotografeerde open dozen wel (anders rekt calibrateOnce de doos ongelijk op). */
    const xy = !!obj.rotXY;
    obj.group.traverse(o => {
      if (!o.geometry || (o.userData && o.userData.isEffect)) return;
      any = true;
      const corners = meshLocalCorners(o);
      for (let k = 0; k < corners.length; k++) {
        const lx = corners[k].x, ly = corners[k].y, lz = corners[k].z;
        let x2, y1, z2;
        if (xy) {
          const xa = lx * cy + lz * sy, za = -lx * sy + lz * cy;
          x2 = xa; y1 = ly * cp - za * sp; z2 = ly * sp + za * cp;
        } else {
          y1 = ly * cp - lz * sp; const z1 = ly * sp + lz * cp;
          x2 = lx * cy + z1 * sy; z2 = -lx * sy + z1 * cy;
        }
        const wx = sc * (x2 + Tx), wy = sc * (y1 + Ty), wz = sc * z2;
        const depth = Math.max(.05, dist - wz);
        const ndcX = wx / depth / tanX, ndcY = wy / depth / tanY;
        const px = (.5 + .5 * ndcX) * canvasW, py = (.5 - .5 * ndcY) * canvasH;
        if (px < minX) minX = px; if (px > maxX) maxX = px; if (py < minY) minY = py; if (py > maxY) maxY = py;
      }
    });
    if (!any) return projectedBox(obj, sc, Tx, Ty, canvasW, canvasH);
    return { minX, maxX, minY, maxY, w: maxX - minX, h: maxY - minY, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
  }
  /* lost (sc,Tx,Ty) op zodat de geprojecteerde doos target (CSS px, canvas-assenstelsel) vult: een
     paar fixed-point-iteraties volstaan (de relatie is bijna lineair voor een dieptebereik klein t.o.v.
     de cameraafstand), elke iteratie her-projecteert dus geen enkele benadering stapelt op. Gebruikt
     realProjectedBox (de ECHTE meshgeometrie), niet de idealiseerde doos: zo sluit de solve op wat er
     werkelijk gerenderd wordt, ook als een mesh (pack-bulge, afgeronde hoeken, ...) van een perfecte
     doos afwijkt. */
  function solveFrame(obj, canvasW, canvasH, target) {
    const dist = camera.position.z, tanY = Math.tan(T2.MathUtils.degToRad(camera.fov / 2)), tanX = tanY * camera.aspect;
    let sc = 1, Tx = 0, Ty = obj.h * .03;
    /* ruim meer iteraties dan het "bijna lineair" vermoeden hierboven aangaf: de dieptecorrectie per
       hoekpunt verschilt (yaw/pitch geven elk hoekpunt een andere z, dus een andere depth), dus de
       Tx/Ty-stap hieronder is een BENADERING (uniforme depth=dist) die moet FIXPOINT-convergeren i.p.v.
       in 1 stap exact te raken; met te weinig iteraties bleef een restfout van een paar procent over. */
    for (let i = 0; i < 40; i++) {
      const p = realProjectedBox(obj, sc, Tx, Ty, canvasW, canvasH);
      if (p.w > 1e-3 && p.h > 1e-3) sc *= obj.contain ? Math.min(target.w / p.w, target.h / p.h) : (target.w / p.w + target.h / p.h) / 2;
      const p2 = realProjectedBox(obj, sc, Tx, Ty, canvasW, canvasH);
      const targetCx = target.x + target.w / 2, targetCy = target.y + target.h / 2;
      const dNdcX = (targetCx - p2.cx) / canvasW * 2, dNdcY = -(targetCy - p2.cy) / canvasH * 2;
      Tx += dNdcX * tanX * dist / Math.max(.05, sc); Ty += dNdcY * tanY * dist / Math.max(.05, sc);
    }
    return { sc: clamp(sc, .05, 20), Tx, Ty };
  }
  /* ================= laatste stap van de closed loop: een ECHTE pixelmeting =================
     realProjectedBox (hierboven) is een geometrisch model: nauwkeurig voor een BoxGeometry (die is
     exact de mesh), maar een textuur-alphaTest-rand (pack-pillow) kan op het scherm een fractie
     scherper/zachter uitvallen dan het model aanneemt (mipmap-filtering bij een kleine rendergrootte,
     de software-rasterizer, ...) -- een effect dat geen enkel geometrisch model vooraf kan voorspellen.
     Daarom: na de EERSTE render van een product een echte pixelmeting (WebGL readPixels, geen
     toDataURL/canvas2d-omweg nodig: synchroon lezen net na render() werkt altijd, ook zonder
     preserveDrawingBuffer, er is nog niets gewisseld/gewist), vergeleken met het doel. Het resultaat
     (niet-uniforme schaal + een vaste wereldruimte-positiebijstelling) wordt 1x per product-object
     bewaard (obj.calib) en bij elke layout() opnieuw toegepast bovenop een VERSE, calib-onafhankelijke
     solveFrame-oplossing -- dus niet opnieuw gerenderd/gelezen bij elke resize. Draait met
     idle/hover/drag/enter op nul (de heldpose zelf), exact het moment dat realProjectedBox ook
     aanneemt. */
  /* alfa-kader van een offscreen-doel, terug in CSS px van het canvas (cssW = canvasbreedte in CSS px) */
  function readTargetAlphaBBox(rt, cssW) {
    try {
      const W = rt.width, H = rt.height, buf = new Uint8Array(W * H * 4);
      renderer.readRenderTargetPixels(rt, 0, 0, W, H, buf);
      let minX = W, maxX = -1, minYgl = H, maxYgl = -1;
      for (let y = 0; y < H; y++) { const row = y * W; for (let x = 0; x < W; x++) { if (buf[(row + x) * 4 + 3] > 128) { if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minYgl) minYgl = y; if (y > maxYgl) maxYgl = y; } } }
      if (maxX < 0) return null;
      const k = cssW / W, minY = H - 1 - maxYgl, maxY = H - 1 - minYgl;
      return { x: minX * k, y: minY * k, w: (maxX + 1 - minX) * k, h: (maxY + 1 - minY) * k };
    } catch (e) { return null; }
  }
  function readRenderAlphaBBox() {
    try {
      const gl = renderer.getContext(); const cv = renderer.domElement;
      const W = cv.width, H = cv.height; if (!W || !H) return null;
      const buf = new Uint8Array(W * H * 4);
      gl.readPixels(0, 0, W, H, gl.RGBA, gl.UNSIGNED_BYTE, buf);
      let minX = W, maxX = -1, minYgl = H, maxYgl = -1; const TH = 128, STEP = 2;
      for (let y = 0; y < H; y += STEP) {
        const row = y * W;
        for (let x = 0; x < W; x += STEP) {
          if (buf[(row + x) * 4 + 3] > TH) { if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minYgl) minYgl = y; if (y > maxYgl) maxYgl = y; }
        }
      }
      if (maxX < 0) return null;
      const minY = H - 1 - maxYgl, maxY = H - 1 - minYgl; /* gl: rij 0 = onderaan; CSS: rij 0 = bovenaan */
      const ratio = W / (cv.clientWidth || 1);
      return { x: minX / ratio, y: minY / ratio, w: (maxX - minX) / ratio, h: (maxY - minY) / ratio };
    } catch (e) { return null; }
  }
  function calibrateOnce(obj, canvasW, canvasH, target) {
    obj.calibDone = true; /* altijd markeren, ook bij falen: nooit blijven herproberen */
    if (!renderer) return;
    const dist = camera.position.z, tanY = Math.tan(T2.MathUtils.degToRad(camera.fov / 2)), tanX = tanY * camera.aspect;
    const renderAt = (wr, hr, bx, by) => {
      obj.group.scale.set(wr, hr, 1);
      holder.rotation.set((obj.heroPitch || 0) - .01, obj.heroYaw || 0, 0);
      holder.scale.setScalar(1); holder.position.set(bx, by, 0);
      /* camera.lookAt: tick() roept dit ELKE frame aan (camera.position.y staat vast op .08, dus
         lookAt(0,0,0) geeft een kleine blijvende pitch-tilt van de camera zelf) -- vond dit HIER niet
         plaats, dan rendert calibratie met de camera in zijn init()-starthouding (quaternion identity,
         geen tilt) zolang tick() voor dit product nog geen EERSTE frame draaide. Dat gaf een
         systematische verticale mismatch t.o.v. de echte (wel-getilte) render die de gebruiker te zien
         krijgt (geverifieerd: calibratie mat de camera-quaternion [0,0,0,1], de echte render
         [-0.0065,0,0,0.99998] -- een klein maar genoeg verschil voor een paar procent op het centrum). */
      camera.lookAt(0, 0, 0);
      /* meten in een klein offscreen-doel (640 px breed, zelfde beeldverhouding als het canvas) i.p.v.
         het volledige canvas: readPixels op het canvas las op DPR 2 telkens ~24 MB terug en blokkeerde
         de hoofdthread (~40 ms per kalibratie). Het eindbeeld wordt hier niet geraakt. */
      try { renderer.setRenderTarget(rt); renderer.render(scene, camera); } catch (e) { renderer.setRenderTarget(null); return null; }
      const m = readTargetAlphaBBox(rt, canvasW); renderer.setRenderTarget(null); return m;
    };
    const RW = 640, RH = Math.max(32, Math.round(RW * canvasH / canvasW));
    if (!calRT) calRT = new T2.WebGLRenderTarget(RW, RH); else if (calRT.width !== RW || calRT.height !== RH) calRT.setSize(RW, RH);
    const rt = calRT;
    /* opwarmrenders: de EERSTE render(s) net na het aanmaken van een textuur geven op sommige
       WebGL-implementaties (bv. de software-rasterizer hier) nog een afwijkend (nog niet volledig
       gemipmapt) resultaat t.o.v. de ingeburgerde staat een fractie later -- zonder opwarming mat de
       hele kalibratielus tegen een nog verschuivende bodem, en kwam de uiteindelijke correctie
       structureel verkeerd uit (geverifieerd: 5x identiek herrenderen ogenblikkelijk na het laden gaf
       een ANDER, stabiel getal dan de eerste render). 2 weggegooide renders volstonden in de test. */
    renderAt(1, 1, S.baseX, S.baseY);
    /* NIET-uniforme restcorrectie (los per as): een enkele uniforme schaal kan een asymmetrische
       afwijking (bv. enkel de breedte te krap door een textuur-filterrand) niet wegwerken zonder de
       andere as juist slechter te maken. Begrensd (+/-18%): een echte pose/geometriefout hoort hier
       niet uitvergroot te worden, enkel kleine rand-/filtereffecten gecorrigeerd.
       Schaal EN positie samen itereren (niet schaal eerst, dan 1x positie erachteraan): bij een
       gekanteld vlak/doos ligt niet elk hoekpunt op dezelfde diepte, dus een positie-verschuiving
       verandert via het perspectief ook een beetje de GEPROJECTEERDE maat -- schaal en positie na
       elkaar apart oplossen liet daardoor net na de laatste positie-correctie weer een restfout van
       een paar procent in de maat staan. Samen itereren (net als solveFrame hierboven) convergeert
       wel naar een zelf-consistente eindstand. */
    const MAXIT = 5;
    let wRatio = 1, hRatio = 1, bx = S.baseX, by = S.baseY, measured = null;
    for (let i = 0; i < MAXIT; i++) {
      measured = renderAt(wRatio, hRatio, bx, by);
      if (!measured || !(measured.w > 1) || !(measured.h > 1)) { obj.group.scale.set(1, 1, 1); return; }
      /* de LAATSTE iteratie berekent geen nieuwe update meer: wRatio/hRatio/bx/by die hierna bewaard
         worden, zijn dan exact wat de net uitgevoerde (en gemeten) render gebruikte, nooit een
         ongeteste extrapolatie een stap verder dan ooit echt gerenderd is -- anders bewaarde
         obj.calib een stap-voor-de-muziek-uit-correctie die in de praktijk net naast de meting viel
         (geverifieerd: dat gaf een paar procent afwijking op het centrum bij bepaalde producten). */
      if (i === MAXIT - 1) break;
      wRatio = clamp(wRatio * (target.w / measured.w), .82, 1.18);
      hRatio = clamp(hRatio * (target.h / measured.h), .82, 1.18);
      const mcx = measured.x + measured.w / 2, mcy = measured.y + measured.h / 2;
      const tcx = target.x + target.w / 2, tcy = target.y + target.h / 2;
      const dNdcX = (tcx - mcx) / canvasW * 2, dNdcY = -(tcy - mcy) / canvasH * 2;
      bx += dNdcX * tanX * dist / Math.max(.05, root.scale.x);
      by += dNdcY * tanY * dist / Math.max(.05, root.scale.x);
    }
    /* obj.calib: PURE empirische restcorrectie, los van de geometrische solve (zie de uitleg boven
       realProjectedBox). wRatio/hRatio -> cur.group.scale (elke layout()-aanroep opnieuw gezet).
       dTx/dTy -> een VASTE wereldruimte-bijstelling bovenop solveFrame's (altijd stabiele, want nu
       calib-onafhankelijke) Tx/Ty, ELKE layout()-aanroep opnieuw opgeteld bij een VERSE solveFrame-
       uitkomst (dus geen dubbeltelling: solveFrame geeft nooit twee keer hetzelfde cumulatieve
       antwoord, het is elke keer een frisse oplossing + dezelfde vaste correctie erbovenop). */
    obj.calib = { wRatio, hRatio, dTx: bx - S.baseX, dTy: by - S.baseY };
    obj.group.scale.set(wRatio, hRatio, 1);
    S.baseX = bx; S.baseY = by;
  }
  function layout() {
    if (!cur) return; S.stillFrames = 8;
    if (cur.targetFrac && host) {
      const r = boxOf(host);
      if (r.width > 4 && r.height > 4) {
        const target = { x: cur.targetFrac.x * r.width, y: cur.targetFrac.y * r.height, w: cur.targetFrac.w * r.width, h: cur.targetFrac.h * r.height };
        const f = solveFrame(cur, r.width, r.height, target);
        root.scale.setScalar(f.sc); S.baseX = f.Tx; S.baseY = f.Ty;
        if (!cur.calibDone) calibrateOnce(cur, r.width, r.height, target);
        else if (cur.calib) { cur.group.scale.set(cur.calib.wRatio, cur.calib.hRatio, 1); S.baseX += cur.calib.dTx; S.baseY += cur.calib.dTy; }
        return;
      }
    }
    S.baseX = 0;
    const VH = 2 * camera.position.z * Math.tan(T2.MathUtils.degToRad(camera.fov / 2)), VW = VH * camera.aspect;
    const yawH = cur.heroYaw || 0, pitchH = cur.heroPitch || 0;
    const projW = Math.abs(cur.w * Math.cos(yawH)) + Math.abs((cur.d || 0) * Math.sin(yawH));
    const projH = Math.abs(cur.h * Math.cos(pitchH)) + Math.abs((cur.d || 0) * Math.sin(pitchH));
    const sc = Math.min(1.3, (VW * FILL) / projW, (VH * FILL) / projH);
    root.scale.setScalar(sc); S.baseY = (VH * .03) / sc;
  }
  function enterPose() {
    if (G) G.killTweensOf(S); S.drag = 0;
    /* volgt de 3D de beweging van de platte foto (follow, gezet door app.js), dan is er geen eigen
       instap: de 3D moet bij de kruisfade exact daar staan waar de foto al staat, geen draai-in. */
    if (RM || S.follow) { S.enter = 1; return; }
    S.enter = 0; if (G) G.to(S, { enter: 1, duration: .55, ease: 'back.out(1.7)' }); else S.enter = 1;
  }
  /* opts.fade (ms, optioneel): het canvas vaagt in zoveel ms over het stilstaande beeld heen i.p.v. in 1 frame te
     wisselen (productpagina met poster, app.js pgShow3D); de foto eronder verbergt de aanroeper dan zelf pas na de fade. */
  async function show(product, container, targetRect, opts) {
    if (!renderer || !container) return false;
    const my = ++token;
    let obj = cache.get(product.h);
    if (!obj) {
      obj = await loadOnce(product);
      if (my !== token) return false;
      if (!obj) { clearStage(container); return false; }
    }
    remember(product.h, obj);
    if (obj.recolor) obj.recolor(product.vimg || null); /* 18 okt (Andrea): gekozen kleurvariant (app.js varSync), laadt eerst en wisselt dan */
    while (holder.children.length) holder.remove(holder.children[0]);
    holder.add(obj.group); cur = obj;
    /* targetRect (CSS px, t.o.v. container) komt van app.js se targetRectFor(): de exacte rechthoek
       die de platte cutout-foto al inneemt. Als FRACTIE bewaard (t.o.v. de containermaat NU) zodat
       resize() hem kan herschalen zonder dat app.js opnieuw moet aanleveren. */
    if (targetRect && container) {
      const r = boxOf(container);
      cur.targetFrac = (r.width > 4 && r.height > 4) ? { x: targetRect.x / r.width, y: targetRect.y / r.height, w: targetRect.w / r.width, h: targetRect.h / r.height } : null;
    } else cur.targetFrac = null;
    /* laatst-geleverde targetRect (CSS px, moment van levering) + container-afmeting op dat moment:
       bewaard zodat retarget() een ECHTE her-meting (nieuwe targetRect van app.js) kan toepassen i.p.v.
       enkel de bestaande fractie te herschalen -- nodig zodra de stage zelf van vorm verandert
       (bv. .info-herontwerp dat de hoogte van .herorow verandert), niet enkel van grootte. */
    if (host !== container) {
      /* eerst onzichtbaar (zonder overgang), dan pas verhuizen: anders toont de nieuwe stage een seconde
         het laatste beeld van de vorige scene (ander product, andere draaiing) boven zijn eigen foto */
      setVis(false);
      if (host) { hostOff(host); ro && ro.unobserve(host); }
      host = container; container.appendChild(renderer.domElement); ro && ro.observe(container);
    }
    resize();
    enterPose();
    if (!raf) { last = performance.now(); raf = requestAnimationFrame(tick); }
    /* pas zichtbaar als er echt een beeld van DIT product getekend is (2 frames: tick heeft gelopen, plus
       hier nog 1 render voor het geval de instantie gepauzeerd staat); klasse en opacity op hetzelfde moment */
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (!renderer || my !== token || host !== container || cur !== obj) return;
      try { renderer.render(scene, camera); } catch (e) {}
      setVis(true, opts && opts.fade > 0 ? +opts.fade : 0);
    }));
    return true;
  }
  /* leegmaken zonder het canvas te verwijderen (voor een product zonder 3D-data): de platte
     foto blijft dan alleen zichtbaar, geen rest van de vorige scene blijft overlappen. */
  /* Dubbel beeld (eigenaar 6 okt: 3D-doosje en vlakke foto tegelijk zichtbaar, het doosje gedraaid met de foto
     erachter uitstekend). Eén bron van waarheid: setVis() zet de opacity van het canvas (zonder overgang) en de
     klasse .stage3d-active op de host in DEZELFDE stap, en de foto (.scard-ph) wisselt zonder overgang mee
     (style.css). Zo is altijd precies een van beide zichtbaar: de foto gaat pas weg als het canvas getekend is,
     het canvas is al weg als de foto terugkomt. hostOff() haalt de klasse enkel weg als er in die container geen
     ander zichtbaar 3D-canvas meer hangt (de andere instantie, Studio/Spare, kan er intussen staan). */
  function hostOff(c) {
    if (!c) return;
    const own = renderer && renderer.domElement;
    const other = [...c.querySelectorAll('.stage3d-canvas')].some(x => x !== own && x.style.opacity === '1');
    if (!other) c.classList.remove('stage3d-active');
  }
  function setVis(on, fade) {
    if (!renderer) return;
    /* fade (ms) enkel bij tonen (show met opts.fade): overgang op de opacity; verbergen blijft altijd in 1 frame */
    const cv = renderer.domElement; cv.style.transition = on && fade > 0 ? 'opacity ' + fade + 'ms ease-out' : 'none';
    if (on) { cv.style.opacity = '1'; if (host) host.classList.add('stage3d-active'); }
    else { cv.style.opacity = '0'; if (host) hostOff(host); }
  }
  function clearStage(container) {
    while (holder.children.length) holder.remove(holder.children[0]);
    cur = null;
    setVis(false); if (container && container !== host) hostOff(container);
    if (host === container) {
      if (renderer && renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
      ro && ro.unobserve(container); host = null;
    }
  }
  function detach() {
    token++; setVis(false); if (host) ro && ro.unobserve(host);
    host = null; cur = null;
    if (renderer && renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
  }
  /* eenzelfde product maar een keer tegelijk opbouwen: een prefetch (hover, app.js pfIntent) en een show() vlak erna
     (de klik) delen dezelfde belofte, anders laadde de klik alles opnieuw en bleef het eerste object onopgeruimd */
  const inflight = new Map();
  function loadOnce(product) {
    const h = product.h; if (inflight.has(h)) return inflight.get(h);
    const pr = loadProduct(product).finally(() => inflight.delete(h));
    inflight.set(h, pr); return pr;
  }
  async function prefetch(product) {
    if (!renderer || cache.has(product.h)) return;
    const obj = await loadOnce(product); if (obj && !cache.has(product.h)) remember(product.h, obj);
  }
  /* cache als LRU, max 5 producten per instantie: met twee instanties en tot 24 producten met 3D zou
     een onbegrensde cache honderden MB aan texturen op de grafische kaart laten staan (mobiel!) */
  function remember(h, obj) {
    cache.delete(h); cache.set(h, obj);
    while (cache.size > 5) {
      const k = [...cache.keys()].find(key => cache.get(key) !== cur && cache.get(key) !== obj);
      if (k == null) break; disposeObj(cache.get(k)); cache.delete(k);
    }
  }
  function disposeObj(obj) {
    if (obj.vFree) obj.vFree();
    obj.group.traverse(o => {
      if (o.geometry) o.geometry.dispose();
      if (o.material) [].concat(o.material).forEach(m => { if (m.map) m.map.dispose(); if (m.userData && m.userData.extraTex) m.userData.extraTex.dispose(); m.dispose(); });
    });
  }
  function disposeOne(id) { const o = cache.get(id); if (o) { if (cur === o) cur = null; disposeObj(o); cache.delete(id); } }
  function bindGyro() {
    if (S.gOn) return; S.gOn = true;
    global.addEventListener('deviceorientation', e => { if (e.beta != null) S.gBeta = e.beta; if (e.gamma != null) S.gGamma = e.gamma; });
  }
  function requestGyro() {
    return new Promise(res => {
      const DOE = global.DeviceOrientationEvent;
      if (DOE && typeof DOE.requestPermission === 'function') DOE.requestPermission().then(r => { if (r === 'granted') { bindGyro(); res(true); } else res(false); }).catch(() => res(false));
      else if (DOE) { bindGyro(); res(true); } else res(false);
    });
  }
  function tick(now) {
    raf = requestAnimationFrame(tick);
    if (S.paused || document.hidden || !cur) return;
    /* stilstaand (klaargezette reserve-scene): enkel een paar frames na show/layout tekenen, daarna
       blijft het laatste beeld staan zonder de grafische kaart elk frame te belasten */
    if (S.still) { if (S.stillFrames <= 0) return; S.stillFrames--; }
    /* 30 fps-plafond op 'low': tussenliggende frames overslaan (de tijd loopt via last gewoon door) */
    if (QL.fps < 60 && !S.still && now - MON.lastRender < 1000 / QL.fps - 4) return;
    /* monitor: echte tijd tussen getekende frames. Zakt de mediaan over 60 frames onder ~25 fps
       (onder ~18 fps met het 30 fps-plafond), dan 20% minder pixels, tot de helft van het niveau. */
    if (MON.lastRender && !S.still) {
      MON.buf.push(now - MON.lastRender);
      if (MON.buf.length >= 60) {
        const med = MON.buf.sort((a, b) => a - b)[30]; MON.buf.length = 0;
        if (med > (QL.fps < 60 ? 55 : 40) && S.prScale > .55) { S.prScale = Math.max(.5, +(S.prScale - .2).toFixed(2)); resize(); }
      }
    }
    MON.lastRender = now;
    const dt = Math.min(.05, Math.max(.001, (now - last) / 1000)); last = now; S.t += dt;
    const idle = RM ? 0 : 1, e = S.enter;
    /* EEN bewegingsbron met de platte foto (app.js tiltFrame): zelfde zweven, zelfde kanteling naar de
       muis, zelfde gladstrijking. Zo is de kruisfade foto -> 3D onzichtbaar: beide staan op elk moment
       in dezelfde pose. ry/rx in graden zoals CSS rotateY/rotateX, dx/dy in CSS px zoals translate. */
    const fol = S.follow ? S.follow() : null;
    if (fol) {
      const DEG = Math.PI / 180;
      const k = (2 * Math.tan(T2.MathUtils.degToRad(camera.fov / 2)) * camera.position.z) / ((S.vh || 1) * (root.scale.x || 1));
      holder.rotation.set((cur.heroPitch || 0) - .01 - fol.rx * DEG, (cur.heroYaw || 0) + S.drag + fol.ry * DEG, 0);
      holder.scale.setScalar(1); holder.position.set(S.baseX + fol.dx * k, S.baseY - fol.dy * k, 0);
    } else {
    /* gyro (mobiel) is de hover-tegenhanger van de muis: zelfde begrenzing, zodat hij nooit meer
       kanteling geeft dan de pointer-tilt hieronder zou geven. */
    const gYaw = (!RM && S.gOn) ? clamp(S.gGamma / 45, -1, 1) * YAW_MAX : 0;
    const gPitch = (!RM && S.gOn) ? clamp((S.gBeta - 45) / 45, -1, 1) * PITCH_MAX : 0;
    /* pointer/hover-tilt: kleine, begrensde offset BOVENOP de heldpose (nooit een vervanging).
       S.px/py staan op 0 zodra de muis de stage verlaat (app.js) of bij reduced-motion (hieronder
       geforceerd), dus de pose veert dan terug naar de heldpose zelf, niet naar yaw 0. Gelijmd via
       lerp zodat het geen sprong geeft, enkel een zachte volg-tilt. */
    const tgtYaw = RM ? 0 : clamp(S.px * 2, -1, 1) * YAW_MAX;
    const tgtPitch = RM ? 0 : clamp(S.py * 2, -1, 1) * PITCH_MAX;
    S.hoverYaw += (tgtYaw - S.hoverYaw) * .08;
    S.hoverPitch += (tgtPitch - S.hoverPitch) * .08;
    /* idle-zweefdeining: subtiel, niet de dominante beweging -- anders overschrijdt die op zich al
       de hover-tiltbudget hierboven en lijkt de kadrering "toevallig" mee te veranderen. */
    const yaw = (cur.heroYaw || 0) + idle * Math.sin(S.t * .5) * .035 + S.drag + S.hoverYaw + gYaw + (1 - e) * -1.1;
    const pitch = (cur.heroPitch || 0) + idle * Math.sin(S.t * .4) * .025 - .01 + S.hoverPitch + gPitch;
    const bob = idle * Math.sin(S.t * 1.02) * .045, sc = .82 + .18 * e;
    holder.rotation.set(pitch, yaw, idle * Math.sin(S.t * .3) * .012);
    holder.scale.setScalar(sc); holder.position.set(S.baseX, S.baseY + bob + (1 - e) * -.22, 0);
    }
    if (cur.sweep) { cur.sweepT += dt; const ph = (cur.sweepT % 5.4) / 1.6; cur.sweep.material.uniforms.prog.value = ph <= 1 ? -.7 + ph * 2.6 : 6; }
    if (dust) {
      const pos = dust.geometry.attributes.position, sp = dust.geometry.userData.sp;
      if (idle) { for (let i = 0; i < sp.length; i++) { let y = pos.getY(i) + sp[i] * dt; if (y > 1.7) y = -1.6; pos.setY(i, y); } pos.needsUpdate = true; }
    }
    /* geen zijwaartse camera-pan meer op de pointer: dat verschoof de kadrering zelf (tegen regel 2
       hierboven), de tilt zit nu enkel in holder.rotation (S.hoverYaw/hoverPitch). Camera blijft op
       zijn vaste positie, veert terug als hij door een oudere pose nog niet op 0 staat. */
    camera.position.x += (0 - camera.position.x) * .08; camera.lookAt(0, 0, 0);
    renderer.render(scene, camera);
  }
  return {
    init, show, detach, prefetch, dispose: disposeOne,
    /* 18 okt (Andrea): kleurvariant van een al gebouwd model wisselen (product.vimg = variantfoto of null), zonder herbouw */
    recolor(product) { const o = cache.get(product.h); return o && o.recolor ? o.recolor(product.vimg || null) : Promise.resolve(false); },
    /* sleep-draai: begrensd tot +/-DRAG_MAX rond de heldpose (regel "verder binnen de bestaande
       grenzen"). dragEnd rondt altijd af naar het dichtstbijzijnde veelvoud van 2*PI, wat binnen dit
       bereik altijd 0 is: hij veert dus terug naar de heldpose zelf, nooit naar yaw 0. */
    dragStart() { if (G) G.killTweensOf(S, 'drag'); /* 16 okt (Andrea: dubbel product bij snel draaien): loopt de kruisfade over de foto nog, dan meteen volledig zichtbaar, anders draait een halfdoorzichtig model over de stilstaande foto */ if (renderer) { const cv = renderer.domElement; if (cv.style.transition && cv.style.transition !== 'none') cv.style.transition = 'none'; } }, drag(dx) { S.drag = clamp(S.drag + dx * .011, -DRAG_MAX, DRAG_MAX); },
    dragEnd(v) { const proj = clamp(S.drag + (v || 0) * .011 * .3, -DRAG_MAX * 1.2, DRAG_MAX * 1.2); if (G) G.to(S, { drag: Math.round(proj / (Math.PI * 2)) * Math.PI * 2, duration: 1.7, ease: 'elastic.out(1,0.42)' }); else S.drag = 0; },
    /* x/y: genormaliseerd -.5..5 TEN OPZICHTE VAN DE STAGE-CONTAINER (0,0 = gecentreerd of weg van
       de stage), gezet door app.js. Hier enkel nog verduidelijkt + defensief begrensd. */
    pointer(x, y) { S.px = clamp(x, -.5, .5); S.py = clamp(y, -.5, .5); },
    /* fn() -> { ry, rx, dx, dy }: neemt de pose van de platte foto over (zie tick) */
    follow(fn) { S.follow = typeof fn === 'function' ? fn : null; },
    /* still(true): klaargezette reserve, enkel nog tekenen na show/layout (zie tick) */
    still(b) { S.still = !!b; S.stillFrames = 8; if (!b) { last = performance.now(); MON.lastRender = 0; MON.buf.length = 0; } },
    /* gekozen kwaliteitsniveau (+ huidige automatische pixelverlaging), voor app.js en tests */
    quality() { return Object.assign({ prScale: S.prScale, gpu: detectTier().gpu }, QL); },
    requestGyro, pause(b) { S.paused = b; if (!b) last = performance.now(); },
    pose(r) { if (G) G.killTweensOf(S); S.enter = 1; S.drag = clamp(r, -DRAG_MAX, DRAG_MAX); },
    owns(container) { return host === container; },
    /* yaw van de heldpose van het getoonde product (radialen): app.js draait bij hover precies zoveel terug, recht naar de klant */
    heroYaw() { return cur ? (cur.heroYaw || 0) : 0; },
    state() { return { active: !!cur, hostAttached: !!host, drag: +S.drag.toFixed(3), hoverYaw: +S.hoverYaw.toFixed(3), cacheSize: cache.size, worker: !!WK, sc: root ? +root.scale.x.toFixed(4) : null, calib: cur && cur.calib ? Object.fromEntries(Object.entries(cur.calib).map(([k, v]) => [k, +v.toFixed(4)])) : null }; },
    /* retarget: ECHTE her-meting toepassen zonder de scene opnieuw op te bouwen (geen cache-reload,
       geen enterPose()-herhaling, dus geen zichtbare hap/flits) -- voor app.js om aan te roepen
       wanneer de ACTIEVE stage zelf van vorm verandert (ResizeObserver op .stage, niet enkel
       window-resize), bv. na een .info-herontwerp dat .herorow's hoogte beinvloedt. targetRect: CSS
       px t.o.v. container, zelfde vorm als het 3e argument van show(). Geen effect als deze container
       niet de actieve host is (voorkomt een stale her-meting van een inmiddels verlaten scene). */
    retarget(targetRect, container) {
      if (!cur || !host || (container && host !== container)) return false;
      const r = boxOf(host);
      if (!(r.width > 4 && r.height > 4)) return false;
      cur.targetFrac = targetRect ? { x: targetRect.x / r.width, y: targetRect.y / r.height, w: targetRect.w / r.width, h: targetRect.h / r.height } : null;
      layout();
      return true;
    },
    /* debug/test: de EXACTE heldpose-projectie bij S.baseX/S.baseY (geen bob/hover/entrance-ruis),
       nu via de ECHTE meshgeometrie (realProjectedBox) i.p.v. de idealiseerde doos, PLUS de empirische
       obj.calib-correctie (groottefactor) erbovenop -- realProjectedBox zelf blijft calib-vrij (zie de
       uitleg daar), maar deze debug-uitlezing mag wel het volledige, eerlijke plaatje tonen van wat er
       werkelijk gerenderd wordt, om de solveFrame-wiskunde los van rendering/animatie te verifieren. */
    debugFrame() {
      if (!cur || !host) return null;
      const r = boxOf(host);
      const proj = realProjectedBox(cur, root.scale.x, S.baseX, S.baseY, r.width, r.height);
      if (cur.calib) {
        const wr = cur.calib.wRatio, hr = cur.calib.hRatio, cx0 = proj.cx, cy0 = proj.cy;
        const w2 = proj.w * wr, h2 = proj.h * hr;
        Object.assign(proj, { w: w2, h: h2, minX: cx0 - w2 / 2, maxX: cx0 + w2 / 2, minY: cy0 - h2 / 2, maxY: cy0 + h2 / 2 });
      }
      return { sc: root.scale.x, baseX: S.baseX, baseY: S.baseY, proj };
    },
    /* testharnas-only: schakelt niet-productgebonden scene-elementen (losse achtergrond-stofjes) uit/
       aan, zodat een canvas-alfa-pixelmeting van het PRODUCT zelf niet vervuild raakt door toevallig
       rondzwevende, willekeurig-geseede deeltjes. Verandert niets aan hoe het product zelf rendert. */
    _debugIsolate(on) { if (dust) dust.visible = !on; },
    /* poster: het huidige product exact in rustpose (zelfde kalibratie en kadrering als de eerste frame
       na het tonen), zonder glanslijn of stofjes, als PNG. Wordt vooraf per product gemaakt (posters.js)
       en als startbeeld getoond, zodat de overgang poster -> 3D onzichtbaar is. */
    /* dy (optioneel, radialen): extra draaiing rond de verticale as bovenop de heldpose, voor de draaifilmpjes
       van de 3D Studio (turntables.js). Zonder dy exact de poster-rustpose zoals voorheen. */
    snapshot(dy) {
      if (!cur || !renderer || !host) return null;
      const sw = cur.sweep && cur.sweep.material.uniforms.prog, sv = sw && sw.value; if (sw) sw.value = 6;
      const dv = dust && dust.visible; if (dust) dust.visible = false;
      holder.rotation.set((cur.heroPitch || 0) - .01, (cur.heroYaw || 0) + (+dy || 0), 0); holder.scale.setScalar(1); holder.position.set(S.baseX, S.baseY, 0);
      camera.lookAt(0, 0, 0); renderer.render(scene, camera);
      const url = renderer.domElement.toDataURL('image/png');
      if (sw) sw.value = sv; if (dust) dust.visible = dv;
      return { url, cssW: host.clientWidth, cssH: host.clientHeight };
    }
  };
}
})(window);

